import { useState, useEffect, useRef } from "react";

const DEMO_URL = "https://meetings.hubspot.com/carlos587/workforce-pell-walkthrough";

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function FadeIn({ children, delay = 0, style = {}, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(28px)",
      transition: `opacity 0.72s cubic-bezier(.16,1,.3,1) ${delay}s, transform 0.72s cubic-bezier(.16,1,.3,1) ${delay}s`,
      ...style
    }}>{children}</div>
  );
}

function AnimatedNumber({ target, prefix = "", suffix = "", decimals = 0 }) {
  const [val, setVal] = useState(0);
  const [ref, inView] = useInView(0.3);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const dur = 1600, step = 16;
    const inc = target / (dur / step);
    const t = setInterval(() => {
      start += inc;
      if (start >= target) { setVal(target); clearInterval(t); }
      else setVal(parseFloat(start.toFixed(decimals)));
    }, step);
    return () => clearInterval(t);
  }, [inView, target]);
  return <span ref={ref}>{prefix}{decimals > 0 ? val.toFixed(decimals) : Math.floor(val).toLocaleString()}{suffix}</span>;
}

// ─── DASHBOARD MODAL (primary CTA — multi-step) ───────────────────────────────
function DashboardModal({ onClose }) {
  // step: "form" → "assessment" → "score" → "dashboard"
  const [step, setStep] = useState("form");
  const [form, setForm] = useState({ firstname: "", lastname: "", email: "", company: "", jobtitle: "" });
  const [status, setStatus] = useState("idle");
  const [answers, setAnswers] = useState({ instType: "", programs: "", students: "", employers: "", tracking: "" });
  const [scoreVisible, setScoreVisible] = useState(false);
  const canForm = form.firstname && form.email && form.company;
  const canAssessment = answers.instType && answers.programs && answers.students && answers.employers && answers.tracking;

  const computeScore = () => {
    let s = 0;
    const p = parseInt(answers.programs) || 0;
    const st = parseInt(answers.students) || 0;
    const e = parseInt(answers.employers) || 0;
    if (p >= 5) s += 20; else if (p >= 2) s += 12; else s += 5;
    if (st >= 200) s += 20; else if (st >= 50) s += 13; else s += 6;
    if (e >= 10) s += 20; else if (e >= 3) s += 12; else s += 4;
    if (answers.tracking === "spreadsheets") s += 8;
    else if (answers.tracking === "lms") s += 14;
    else if (answers.tracking === "dedicated") s += 20;
    else s += 2;
    if (answers.instType === "community") s += 12;
    else if (answers.instType === "technical") s += 10;
    else s += 7;
    return Math.min(s, 100);
  };

  const score = computeScore();

  const getScoreLabel = (s) => {
    if (s >= 80) return { label: "Pell-Ready", color: "#22c55e", msg: "Your institution has strong foundational infrastructure. GoSprout helps you optimize compliance and scale." };
    if (s >= 60) return { label: "Emerging Readiness", color: "#F28627", msg: "You have the programs and partners. The missing piece is the operational infrastructure to manage compliance at scale." };
    return { label: "High Opportunity", color: "#fba84a", msg: "Significant untapped potential. GoSprout can help you build the infrastructure needed to access Workforce Pell funding." };
  };

  const submitForm = async () => {
    setStatus("submitting");
    try {
      const res = await fetch("https://api.hsforms.com/submissions/v3/integration/submit/22404677/6bf334b9-408d-4223-9be3-60388a53ca4f", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fields: [
          { name: "firstname", value: form.firstname },
          { name: "lastname", value: form.lastname },
          { name: "email", value: form.email },
          { name: "company", value: form.company },
          { name: "jobtitle", value: form.jobtitle },
          { name: "workforcepellreadinessscore", value: `Score: ${score}/100 | Institution: ${answers.instType} | Programs: ${answers.programs} | Students: ${answers.students} | Employers: ${answers.employers} | Tracking: ${answers.tracking}` },
        ], context: { pageUri: window.location.href, pageName: "GoSprout Workforce Pell Sandbox" } })
      });
      if (res.ok) { setStatus("success"); setStep("assessment"); }
      else setStatus("error");
    } catch { setStatus("error"); }
  };

  useEffect(() => {
    if (step === "score") { setTimeout(() => setScoreVisible(true), 150); }
    else setScoreVisible(false);
  }, [step]);

  const { label: scoreLabel, color: scoreColor, msg: scoreMsg } = getScoreLabel(score);

  const inputStyle = { width:"100%", background:"rgba(255,255,255,.08)", border:"1px solid rgba(255,255,255,.18)", borderRadius:7, padding:"10px 12px", color:"#fff", fontSize:13, fontFamily:"'Nunito Sans',sans-serif", outline:"none", boxSizing:"border-box" };
  const labelStyle = { display:"block", fontSize:10, fontWeight:700, color:"rgba(255,255,255,.7)", textTransform:"uppercase", letterSpacing:".06rem", marginBottom:5 };
  const selectStyle = { ...inputStyle, cursor:"pointer", appearance:"none", WebkitAppearance:"none", color:"#fff" };

  const steps = ["form","assessment","score","dashboard"];
  const stepIdx = steps.indexOf(step);

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div onClick={onClose} style={{ position:"absolute", inset:0, background:"rgba(2,4,12,.92)", backdropFilter:"blur(14px)", cursor:"pointer" }} />
      <div style={{ position:"relative", width:"100%", maxWidth: step === "dashboard" ? 720 : 520, background:"#1b1844", border:"1px solid rgba(242,134,39,.2)", borderRadius:20, overflow:"hidden", boxShadow:"0 40px 120px rgba(0,0,0,.9)", maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ height:3, background:"linear-gradient(90deg,#F28627,#fba84a,#F5A623)" }} />

        {/* Progress dots */}
        <div style={{ display:"flex", justifyContent:"center", gap:8, padding:"16px 0 0" }}>
          {["Account","Assessment","Score","Dashboard"].map((s,i) => (
            <div key={s} style={{ display:"flex", alignItems:"center", gap:4 }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background: i <= stepIdx ? "#F28627" : "rgba(255,255,255,.12)", transition:"background .3s" }} />
              {i < 3 && <div style={{ width:20, height:1, background: i < stepIdx ? "#F28627" : "rgba(255,255,255,.1)" }} />}
            </div>
          ))}
        </div>

        <div className="modal-inner" style={{ padding:"24px 36px 36px" }}>

          {/* ── STEP 1: Account Form ── */}
          {step === "form" && (
            <>
              <div style={{ marginBottom:22 }}>
                <div style={{ display:"inline-flex", alignItems:"center", gap:7, background:"rgba(242,134,39,.1)", border:"1px solid rgba(242,134,39,.2)", borderRadius:20, padding:"4px 12px", marginBottom:12 }}>
                  <span style={{ fontSize:11 }}>🎯</span><span style={{ fontSize:11, color:"#F28627", fontWeight:700, textTransform:"uppercase", letterSpacing:".07rem" }}>Free · No Credit Card · No Commitment</span>
                </div>
                <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:22, color:"#fff", lineHeight:1.25, marginBottom:8 }}>Create Your Free Workforce Pell Sandbox</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,.45)", lineHeight:1.65 }}>See what Workforce Pell management could look like at your institution in under 5 minutes.</div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:10 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }} className="modal-grid">
                  {[["firstname","First Name","Jane"],["lastname","Last Name","Smith"]].map(([k,l,p]) => (
                    <div key={k}><label style={labelStyle}>{l}</label><input placeholder={p} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} style={inputStyle} /></div>
                  ))}
                </div>
                {[["company","Institution","Frederick Community College"],["jobtitle","Your Title","VP of Workforce Education"],["email","Work Email","jane@college.edu"]].map(([k,l,p]) => (
                  <div key={k}><label style={labelStyle}>{l}</label><input type={k==="email"?"email":"text"} placeholder={p} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} style={inputStyle} /></div>
                ))}
              </div>
              {status==="error"&&<div style={{ background:"rgba(239,68,68,.1)", border:"1px solid rgba(239,68,68,.3)", borderRadius:7, padding:"9px 12px", marginBottom:10, fontSize:12, color:"#ef4444" }}>Something went wrong. Please try again.</div>}
              <button onClick={submitForm} disabled={!canForm||status==="submitting"} style={{ width:"100%", background:canForm?"#F28627":"rgba(255,255,255,.07)", border:"none", color:canForm?"#fff":"rgba(255,255,255,.2)", fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:14, padding:"14px", borderRadius:8, cursor:canForm?"pointer":"not-allowed", transition:"all .2s", marginTop:4 }}>
                {status==="submitting"?"Setting up your dashboard…":"Continue → Readiness Assessment"}
              </button>
              <div style={{ marginTop:10, fontSize:11, color:"rgba(255,255,255,.2)", textAlign:"center" }}>
                You'll receive: Readiness Score · Opportunity Calculator · Executive Dashboard · Compliance Checklist · Toolkit
              </div>
            </>
          )}

          {/* ── STEP 2: Assessment ── */}
          {step === "assessment" && (
            <>
              <div style={{ marginBottom:22 }}>
                <div style={{ display:"inline-flex", alignItems:"center", gap:7, background:"rgba(242,134,39,.1)", border:"1px solid rgba(242,134,39,.2)", borderRadius:20, padding:"4px 12px", marginBottom:12 }}>
                  <span style={{ fontSize:11 }}>📊</span><span style={{ fontSize:11, color:"#F28627", fontWeight:700, textTransform:"uppercase", letterSpacing:".07rem" }}>2 min · Workforce Pell Readiness Assessment</span>
                </div>
                <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:20, color:"#fff", lineHeight:1.3, marginBottom:6 }}>Tell Us About Your Institution</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,.4)", lineHeight:1.6 }}>We'll generate your personalized Workforce Pell Readiness Score and identify your biggest opportunities.</div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:20 }}>
                <div>
                  <label style={labelStyle}>Institution Type</label>
                  <select value={answers.instType} onChange={e=>setAnswers(a=>({...a,instType:e.target.value}))} style={selectStyle}>
                    <option value="">Select type…</option>
                    <option value="community">Community College</option>
                    <option value="technical">Technical / Career College</option>
                    <option value="continuing">Continuing Education / Workforce Center</option>
                    <option value="university">University / 4-Year Institution</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Number of Workforce Programs</label>
                  <select value={answers.programs} onChange={e=>setAnswers(a=>({...a,programs:e.target.value}))} style={selectStyle}>
                    <option value="">Select range…</option>
                    <option value="1">1 program</option>
                    <option value="3">2–5 programs</option>
                    <option value="8">6–10 programs</option>
                    <option value="15">11+ programs</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Current Workforce Enrollment (students)</label>
                  <select value={answers.students} onChange={e=>setAnswers(a=>({...a,students:e.target.value}))} style={selectStyle}>
                    <option value="">Select range…</option>
                    <option value="25">Fewer than 50</option>
                    <option value="100">50–200</option>
                    <option value="300">200–500</option>
                    <option value="600">500+</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Active Employer Partners</label>
                  <select value={answers.employers} onChange={e=>setAnswers(a=>({...a,employers:e.target.value}))} style={selectStyle}>
                    <option value="">Select range…</option>
                    <option value="1">None yet</option>
                    <option value="3">1–5</option>
                    <option value="8">6–15</option>
                    <option value="20">16+</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Current Tracking Method</label>
                  <select value={answers.tracking} onChange={e=>setAnswers(a=>({...a,tracking:e.target.value}))} style={selectStyle}>
                    <option value="">Select method…</option>
                    <option value="none">No formal tracking</option>
                    <option value="spreadsheets">Spreadsheets / manual processes</option>
                    <option value="lms">LMS only</option>
                    <option value="dedicated">Dedicated workforce management software</option>
                  </select>
                </div>
              </div>
              <button onClick={()=>setStep("score")} disabled={!canAssessment} style={{ width:"100%", background:canAssessment?"#F28627":"rgba(255,255,255,.07)", border:"none", color:canAssessment?"#fff":"rgba(255,255,255,.2)", fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:14, padding:"14px", borderRadius:8, cursor:canAssessment?"pointer":"not-allowed", transition:"all .2s" }}>
                Generate My Readiness Score →
              </button>
            </>
          )}

          {/* ── STEP 3: Score ── */}
          {step === "score" && (
            <>
              <div style={{ textAlign:"center", marginBottom:28 }}>
                <div style={{ fontSize:11, color:"#F28627", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".08rem", marginBottom:16 }}>Your Workforce Pell Readiness Score</div>
                {/* Animated score ring */}
                <div style={{ position:"relative", width:160, height:160, margin:"0 auto 20px" }}>
                  <svg width="160" height="160" viewBox="0 0 160 160" style={{ transform:"rotate(-90deg)" }}>
                    <circle cx="80" cy="80" r="68" fill="none" stroke="rgba(255,255,255,.07)" strokeWidth="10" />
                    <circle cx="80" cy="80" r="68" fill="none" stroke={scoreColor} strokeWidth="10"
                      strokeDasharray={`${2*Math.PI*68}`}
                      strokeDashoffset={scoreVisible ? `${2*Math.PI*68*(1-score/100)}` : `${2*Math.PI*68}`}
                      strokeLinecap="round"
                      style={{ transition:"stroke-dashoffset 1.4s cubic-bezier(.16,1,.3,1) .2s" }} />
                  </svg>
                  <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                    <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:40, color:scoreColor, lineHeight:1 }}>{score}</div>
                    <div style={{ fontSize:13, color:"rgba(255,255,255,.35)" }}>/ 100</div>
                  </div>
                </div>
                <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:20, color:"#fff", marginBottom:8 }}>{scoreLabel}</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,.45)", lineHeight:1.65, maxWidth:340, margin:"0 auto" }}>{scoreMsg}</div>
              </div>

              {/* Gaps identified */}
              <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:12, padding:"20px", marginBottom:20 }}>
                <div style={{ fontSize:11, color:"#F28627", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", marginBottom:14 }}>Gaps Identified</div>
                {[
                  answers.tracking !== "dedicated" && "Compliance tracking: manual processes create audit exposure",
                  parseInt(answers.employers) < 6 && "Employer network: GE metrics require strong placement partnerships",
                  answers.instType === "continuing" && "Program structure: clock-hour SAP automation needed for Pell eligibility",
                  parseInt(answers.programs) < 3 && "Program breadth: more pathways = more Pell revenue potential",
                ].filter(Boolean).slice(0,3).map((gap,i) => (
                  <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:i<2?10:0 }}>
                    <span style={{ color:"#F28627", flexShrink:0, fontSize:12, marginTop:2 }}>→</span>
                    <span style={{ fontSize:13, color:"rgba(255,255,255,.55)", lineHeight:1.5 }}>{gap}</span>
                  </div>
                ))}
              </div>

              <button onClick={() => window.open(DEMO_URL, "_blank")} style={{ width:"100%", background:"#F28627", border:"none", color:"#fff", fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:14, padding:"14px", borderRadius:8, cursor:"pointer", marginBottom:8 }}>
                Schedule Your Workforce Pell Walkthrough Now →
              </button>
              <div style={{ fontSize:11, color:"rgba(255,255,255,.2)", textAlign:"center" }}>We'll build your custom dashboard together on the call</div>
            </>
          )}

          {/* ── STEP 4: Executive Dashboard Preview ── */}
          {step === "dashboard" && (
            <>
              <div style={{ marginBottom:20 }}>
                <div style={{ display:"inline-flex", alignItems:"center", gap:7, background:"rgba(52,211,153,.08)", border:"1px solid rgba(52,211,153,.2)", borderRadius:20, padding:"4px 12px", marginBottom:12 }}>
                  <span style={{ fontSize:11 }}>✅</span><span style={{ fontSize:11, color:"#34d399", fontWeight:700, textTransform:"uppercase", letterSpacing:".07rem" }}>Your Sandbox Is Ready</span>
                </div>
                <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:20, color:"#fff", lineHeight:1.25, marginBottom:6 }}>Workforce Pell Executive Dashboard</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,.4)" }}>Pre-loaded with sample data. This is what your team would see every morning.</div>
              </div>

              {/* Mini dashboard */}
              <div style={{ background:"#16143a", border:"1px solid rgba(255,255,255,.08)", borderRadius:14, overflow:"hidden", marginBottom:20 }}>
                {/* Browser bar */}
                <div style={{ background:"#0f0d2a", padding:"8px 14px", display:"flex", alignItems:"center", gap:6, borderBottom:"1px solid rgba(255,255,255,.05)" }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:"#ef4444" }} /><div style={{ width:8, height:8, borderRadius:"50%", background:"#fbbf24" }} /><div style={{ width:8, height:8, borderRadius:"50%", background:"#22c55e" }} />
                  <div style={{ flex:1, background:"rgba(255,255,255,.04)", borderRadius:4, padding:"3px 10px", marginLeft:6, fontSize:9, color:"rgba(255,255,255,.2)", fontFamily:"'Montserrat',sans-serif" }}>app.gosprout.io/workforce-pell-dashboard</div>
                </div>
                {/* Header */}
                <div style={{ padding:"12px 16px", borderBottom:"1px solid rgba(255,255,255,.05)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div>
                    <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", marginBottom:2 }}>WORKFORCE PELL EXECUTIVE DASHBOARD</div>
                    <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:12, color:"#fff" }}>{form.company || "Your Institution"} · Spring 2026</div>
                  </div>
                  <div style={{ background:"rgba(52,211,153,.1)", border:"1px solid rgba(52,211,153,.2)", borderRadius:12, padding:"3px 8px", fontSize:9, color:"#34d399", fontWeight:700 }}>COMPLIANCE: GREEN ●</div>
                </div>
                {/* KPI grid */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", borderBottom:"1px solid rgba(255,255,255,.05)" }}>
                  {[
                    { label:"Enrollment", val:"247 Students", icon:"👥" },
                    { label:"Completion Rate", val:"91%", icon:"🎓" },
                    { label:"Placement Rate", val:"87%", icon:"💼" },
                  ].map((m,i) => (
                    <div key={m.label} style={{ padding:"12px 14px", borderRight:i<2?"1px solid rgba(255,255,255,.05)":"none", textAlign:"center" }}>
                      <div style={{ fontSize:16, marginBottom:4 }}>{m.icon}</div>
                      <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:15, color:"#F28627", lineHeight:1, marginBottom:3 }}>{m.val}</div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".04rem" }}>{m.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", borderBottom:"1px solid rgba(255,255,255,.05)" }}>
                  {[
                    { label:"Credential Attainment", val:"93%", icon:"📜" },
                    { label:"Employer Partners", val:"22 Employers", icon:"🏭" },
                    { label:"Compliance Status", val:"Green ✅", icon:"🛡️" },
                  ].map((m,i) => (
                    <div key={m.label} style={{ padding:"12px 14px", borderRight:i<2?"1px solid rgba(255,255,255,.05)":"none", textAlign:"center" }}>
                      <div style={{ fontSize:16, marginBottom:4 }}>{m.icon}</div>
                      <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:15, color:"#fba84a", lineHeight:1, marginBottom:3 }}>{m.val}</div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".04rem" }}>{m.label}</div>
                    </div>
                  ))}
                </div>
                {/* Compliance checklist */}
                <div style={{ padding:"12px 16px" }}>
                  <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".05rem", marginBottom:10 }}>Sample Compliance Dashboard</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                    {["Student Progress","SAP Monitoring","Employer Participation","Completion Tracking","Outcome Tracking","Audit Readiness"].map(item => (
                      <div key={item} style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:"rgba(255,255,255,.6)" }}>
                        <span style={{ color:"#22c55e", fontSize:11 }}>✓</span>{item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* What's included */}
              <div style={{ background:"rgba(242,134,39,.05)", border:"1px solid rgba(242,134,39,.15)", borderRadius:12, padding:"16px 18px", marginBottom:20 }}>
                <div style={{ fontSize:11, color:"#F28627", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", marginBottom:12 }}>Your Sandbox Includes</div>
                {[
                  ["✓","Workforce Pell Readiness Score","72 / 100, with gap analysis"],
                  ["✓","Enrollment Opportunity Calculator","Estimate potential revenue and student impact"],
                  ["✓","Executive Workforce Pell Dashboard","Pre-loaded with sample data"],
                  ["✓","Compliance Checklist","Every requirement, tracked automatically"],
                  ["✓","Workforce Pell Readiness Toolkit","Checklists, templates, and launch roadmap"],
                ].map(([icon,title,sub],i) => (
                  <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:i<4?10:0 }}>
                    <span style={{ color:"#F28627", fontSize:13, flexShrink:0 }}>{icon}</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:"rgba(255,255,255,.85)" }}>{title}</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,.35)" }}>{sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={() => window.open(DEMO_URL, "_blank")} style={{ width:"100%", background:"#F28627", border:"none", color:"#fff", fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:14, padding:"14px", borderRadius:8, cursor:"pointer", marginBottom:8 }}>
                Talk to a Workforce Pell Expert →
              </button>
              <button onClick={onClose} style={{ width:"100%", background:"rgba(255,255,255,.05)", border:"1px solid rgba(255,255,255,.1)", color:"rgba(255,255,255,.6)", fontFamily:"'Nunito Sans',sans-serif", fontWeight:600, fontSize:13, padding:"12px", borderRadius:8, cursor:"pointer" }}>
                Explore the Page First
              </button>
            </>
          )}
        </div>

        <button onClick={onClose} style={{ position:"absolute", top:16, right:16, background:"rgba(255,255,255,.05)", border:"none", color:"rgba(255,255,255,.4)", width:28, height:28, borderRadius:"50%", cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
      </div>
    </div>
  );
}

// ─── OUTCOME DASHBOARD MOCK ───────────────────────────────────────────────────
function DashboardMock() {
  const [ref, inView] = useInView(0.15);
  const metrics = [
    { label:"Enrolled", val:247, suffix:"", color:"#F28627", max:300 },
    { label:"On Track", val:91, suffix:"%", color:"#F28627", max:100 },
    { label:"Placed", val:189, suffix:"", color:"#fba84a", max:247 },
    { label:"Avg. Wage", val:24.80, suffix:"/hr", color:"#FBBF24", prefix:"$", max:40, dec:2 },
  ];
  const programs = [
    { name:"Healthcare CNA Cert.", enrolled:68, complete:94, place:88, status:"✅ Compliant" },
    { name:"CDL Commercial Driver", enrolled:42, complete:88, place:96, status:"✅ Compliant" },
    { name:"Welding Technology", enrolled:54, complete:79, place:82, status:"⚠️ Watch" },
    { name:"HVAC Fundamentals", enrolled:38, complete:91, place:91, status:"✅ Compliant" },
    { name:"Cybersecurity Essentials", enrolled:45, complete:84, place:87, status:"✅ Compliant" },
  ];
  return (
    <div ref={ref} style={{ background:"#16143a", border:"1px solid rgba(255,255,255,.08)", borderRadius:18, overflow:"hidden", boxShadow:"0 40px 100px rgba(0,0,0,.7)", opacity:inView?1:0, transform:inView?"translateY(0)":"translateY(40px)", transition:"opacity 1s cubic-bezier(.16,1,.3,1) .1s, transform 1s cubic-bezier(.16,1,.3,1) .1s" }}>
      {/* Window chrome */}
      <div style={{ background:"#16143a", borderBottom:"1px solid rgba(255,255,255,.05)", padding:"10px 16px", display:"flex", alignItems:"center", gap:8 }}>
        <div style={{ width:10, height:10, borderRadius:"50%", background:"#ef4444" }} />
        <div style={{ width:10, height:10, borderRadius:"50%", background:"#fbbf24" }} />
        <div style={{ width:10, height:10, borderRadius:"50%", background:"#22c55e" }} />
        <div style={{ flex:1, background:"rgba(255,255,255,.04)", borderRadius:6, padding:"4px 12px", marginLeft:8, fontSize:10, color:"rgba(255,255,255,.25)", fontFamily:"'Montserrat',sans-serif" }}>app.gosprout.io / workforce-pell-dashboard</div>
      </div>
      {/* Top bar */}
      <div style={{ padding:"16px 20px", borderBottom:"1px solid rgba(255,255,255,.05)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,.35)", fontFamily:"'Montserrat',sans-serif", marginBottom:3 }}>WORKFORCE PELL PERFORMANCE</div>
          <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:14, color:"#fff" }}>Frederick Community College · Spring 2026</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <div style={{ background:"rgba(52,211,153,.1)", border:"1px solid rgba(52,211,153,.2)", borderRadius:20, padding:"4px 10px", fontSize:10, color:"#F28627", fontWeight:700 }}>● LIVE</div>
          <div style={{ background:"rgba(242,134,39,.1)", border:"1px solid rgba(242,134,39,.15)", borderRadius:20, padding:"4px 10px", fontSize:10, color:"#F28627", fontWeight:700 }}>Pell-Eligible: 5 Programs</div>
        </div>
      </div>
      {/* KPI row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:0, borderBottom:"1px solid rgba(255,255,255,.05)" }}>
        {metrics.map((m, i) => (
          <div key={m.label} style={{ padding:"16px 18px", borderRight:i<3?"1px solid rgba(255,255,255,.05)":"none" }}>
            <div style={{ fontSize:10, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".06rem", marginBottom:6 }}>{m.label}</div>
            <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:22, color:m.color, lineHeight:1 }}>
              {inView ? <AnimatedNumber target={m.val} prefix={m.prefix||""} suffix={m.suffix} decimals={m.dec||0} /> : <span>{m.prefix||""}{m.val}{m.suffix}</span>}
            </div>
            <div style={{ marginTop:8, height:3, background:"rgba(255,255,255,.06)", borderRadius:2 }}>
              <div style={{ height:"100%", width:inView?`${(m.val/m.max)*100}%`:"0%", background:m.color, borderRadius:2, transition:"width 1.5s cubic-bezier(.16,1,.3,1) .3s", opacity:.7 }} />
            </div>
          </div>
        ))}
      </div>
      {/* Programs table */}
      <div style={{ padding:"0 0 4px" }}>
        <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr", padding:"10px 18px", background:"rgba(255,255,255,.02)" }}>
          {["Program","Enrolled","Completion","Placement","Pell Status"].map(h => (
            <div key={h} style={{ fontSize:9, color:"rgba(255,255,255,.25)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".06rem" }}>{h}</div>
          ))}
        </div>
        {programs.map((p, i) => (
          <div key={p.name} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr", padding:"11px 18px", borderTop:"1px solid rgba(255,255,255,.04)", alignItems:"center" }}>
            <div style={{ fontSize:12, color:"rgba(255,255,255,.75)", fontWeight:500 }}>{p.name}</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,.5)", fontFamily:"'Montserrat',sans-serif" }}>{p.enrolled}</div>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ flex:1, height:4, background:"rgba(255,255,255,.06)", borderRadius:2, overflow:"hidden" }}>
                <div style={{ height:"100%", width:inView?`${p.complete}%`:"0%", background:p.complete>=85?"#F28627":"#FBBF24", borderRadius:2, transition:`width 1.4s cubic-bezier(.16,1,.3,1) ${.2+i*.08}s` }} />
              </div>
              <span style={{ fontSize:10, color:"rgba(255,255,255,.4)", fontFamily:"'Montserrat',sans-serif", minWidth:30 }}>{p.complete}%</span>
            </div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,.5)", fontFamily:"'Montserrat',sans-serif" }}>{p.place}%</div>
            <div style={{ fontSize:10, fontWeight:700 }}>{p.status}</div>
          </div>
        ))}
      </div>
      {/* Bottom bar */}
      <div style={{ padding:"12px 18px", borderTop:"1px solid rgba(255,255,255,.05)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ fontSize:10, color:"rgba(255,255,255,.2)", fontFamily:"'Montserrat',sans-serif" }}>GE Compliance: All programs within threshold · SAP: 234/247 students compliant</div>
        <div style={{ background:"rgba(242,134,39,.12)", border:"1px solid rgba(242,134,39,.2)", borderRadius:6, padding:"5px 10px", fontSize:10, color:"#F28627", fontWeight:700, cursor:"pointer" }}>Export ED Report →</div>
      </div>
    </div>
  );
}

// ─── INSTITUTION CALCULATOR ───────────────────────────────────────────────────
const SCENARIOS = [
  { type:"Community College", enrolled:250, pct:40, avgCost:4000, color:"#F28627" },
  { type:"Technical College", enrolled:150, pct:35, avgCost:5500, color:"#F28627" },
  { type:"Continuing Education", enrolled:400, pct:30, avgCost:3500, color:"#fba84a" },
];

function OpportunityCalc() {
  const [active, setActive] = useState(0);
  const [pct, setPct] = useState(SCENARIOS[0].pct);
  const sc = SCENARIOS[active];
  const eligible = Math.round(sc.enrolled * (pct/100));
  const impact = eligible * sc.avgCost;
  const [ref, inView] = useInView(0.1);

  useEffect(() => { setPct(SCENARIOS[active].pct); }, [active]);

  return (
    <div ref={ref} style={{ opacity:inView?1:0, transform:inView?"translateY(0)":"translateY(28px)", transition:"opacity .8s .1s, transform .8s .1s" }}>
      <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
        {SCENARIOS.map((s,i) => (
          <button key={i} onClick={()=>setActive(i)} style={{ background:active===i?`${s.color}18`:"rgba(255,255,255,.04)", border:`1px solid ${active===i?s.color+"40":"rgba(255,255,255,.08)"}`, borderRadius:8, padding:"8px 16px", color:active===i?s.color:"rgba(255,255,255,.5)", fontFamily:"'Nunito Sans',sans-serif", fontWeight:600, fontSize:13, cursor:"pointer", transition:"all .15s" }}>{s.type}</button>
        ))}
      </div>
      <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:16, padding:"28px 28px 24px" }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:28, alignItems:"start" }}>
          <div>
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:12, color:"rgba(255,255,255,.35)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".05rem", marginBottom:6 }}>Current Short-Term Enrollment</div>
              <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:32, color:"#fff" }}>{sc.enrolled.toLocaleString()} <span style={{ fontSize:16, color:"rgba(255,255,255,.3)", fontWeight:400 }}>students</span></div>
            </div>
            <div style={{ marginBottom:20 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                <div style={{ fontSize:12, color:"rgba(255,255,255,.35)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".05rem" }}>% Likely Pell-Eligible</div>
                <div style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:600, fontSize:13, color:sc.color }}>{pct}%</div>
              </div>
              <input type="range" min={10} max={80} value={pct} onChange={e=>setPct(Number(e.target.value))} style={{ width:"100%", accentColor:sc.color, cursor:"pointer", height:4 }} />
            </div>
            <div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,.35)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".05rem", marginBottom:6 }}>Avg. Program Cost</div>
              <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:18, color:"rgba(255,255,255,.6)" }}>${sc.avgCost.toLocaleString()}</div>
            </div>
          </div>
          <div style={{ background:`linear-gradient(135deg, ${sc.color}0a, ${sc.color}04)`, border:`1px solid ${sc.color}25`, borderRadius:12, padding:"24px 22px" }}>
            <div style={{ fontSize:11, color:sc.color, fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", marginBottom:14 }}>Your Potential Impact</div>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12, color:"rgba(255,255,255,.4)", marginBottom:4 }}>Newly Eligible Students</div>
              <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:36, color:sc.color, lineHeight:1 }}>{eligible.toLocaleString()}</div>
            </div>
            <div style={{ height:1, background:"rgba(255,255,255,.06)", marginBottom:16 }} />
            <div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,.4)", marginBottom:4 }}>Potential Annual Enrollment Impact</div>
              <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:28, color:"#fff", lineHeight:1 }}>${impact >= 1000000 ? `${(impact/1000000).toFixed(1)}M` : `${(impact/1000).toFixed(0)}K`}</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,.3)", marginTop:6, lineHeight:1.5 }}>in previously inaccessible program tuition, now Pell-fundable</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── FAILURE MODES ────────────────────────────────────────────────────────────
const FAILURE_MODES = [
  { belief:"Workforce Pell is a funding initiative", reality:"It's a compliance initiative. Programs that can't prove outcomes lose eligibility.", icon:"💰→⚠️" },
  { belief:"Our spreadsheets can handle the reporting", reality:"GE metrics require cohort-level wage data, completion tracking, and debt ratios — in real time, not at year end.", icon:"📊→💥" },
  { belief:"Our current LMS covers it", reality:"LMSs track learning. Workforce Pell requires employer alignment, OJT documentation, and credential verification — none of which most LMSs do.", icon:"📚→🔲" },
  { belief:"We'll figure out SAP as we go", reality:"Students found non-compliant mid-program lose aid retroactively. SAP must be checked at program midpoints — automatically.", icon:"📋→❌" },
];

// ─── COMPLIANCE BURDEN ────────────────────────────────────────────────────────
const BURDENS = [
  { problem:"Track clock hours vs. credit hours separately across departments", fix:"Unified hour tracking engine handles both — configurable per program" },
  { problem:"Apply SAP checks at mid-program and end-of-program intervals", fix:"Automated SAP workflows with real-time alerts before students fall out" },
  { problem:"Document employer-of-record alignment for each program", fix:"Employer portal captures job alignment, OJT sign-offs, and credential linkage" },
  { problem:"Generate gainful employment outcome data per cohort", fix:"Cohort dashboards with GE metric calculations and exportable templates" },
  { problem:"Prove credential attainment to auditors and accreditors", fix:"Digital credential issuance and verification trail — audit-ready at any time" },
  { problem:"Submit enrollment data to ED systems accurately", fix:"Pre-built NSLDS-compatible export templates, one click" },
];

const NAV = [
  { label:"The Opportunity", href:"#opportunity" },
  { label:"Apprenticeship", href:"#apprenticeship" },
  { label:"Why It's Hard", href:"#challenge" },
  { label:"How GoSprout Helps", href:"#how" },
  { label:"See the Dashboard", href:"#dashboard" },
  { label:"Get Free Sandbox", href:"#cta" },
];

// GoSproutLogo replaced with real PNG logo

// ─── PHOTO PLACEHOLDER ───────────────────────────────────────────────────────
// Replace src with real hosted image URL when available.
// initials + color used as fallback avatar when no photo src provided.
function Avatar({ src, name, size = 48, initials, color = "#F28627" }) {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div style={{ width:size, height:size, borderRadius:"50%", background:`linear-gradient(135deg,${color},${color}99)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.36, fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, color:"#fff", flexShrink:0, border:"2px solid rgba(255,255,255,.12)" }}>
        {initials}
      </div>
    );
  }
  return <img src={src} alt={name} onError={() => setErr(true)} style={{ width:size, height:size, borderRadius:"50%", objectFit:"cover", flexShrink:0, border:"2px solid rgba(255,255,255,.12)" }} />;
}

// Placeholder image block — swap src when real photos available
function PhotoBlock({ src, alt, height = 280, label }) {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div style={{ width:"100%", height, borderRadius:14, background:"linear-gradient(135deg,rgba(242,134,39,.08),rgba(242,134,39,.03))", border:"1px dashed rgba(242,134,39,.25)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:10 }}>
        <div style={{ fontSize:32 }}>📸</div>
        <div style={{ fontSize:11, color:"rgba(255,255,255,.25)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".06rem", textAlign:"center", padding:"0 20px" }}>{label || "Photo placeholder — replace with real image"}</div>
      </div>
    );
  }
  return <img src={src} alt={alt} onError={() => setErr(true)} style={{ width:"100%", height, objectFit:"cover", borderRadius:14, display:"block" }} />;
}

// ─── URGENCY BAR ─────────────────────────────────────────────────────────────
function UrgencyBar({ onDashboard }) {
  const [days, setDays] = useState(0);
  useEffect(() => {
    const deadline = new Date("2026-07-01T00:00:00");
    const update = () => {
      const diff = deadline - new Date();
      setDays(Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24))));
    };
    update();
    const t = setInterval(update, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ background:"linear-gradient(90deg,#1b1844,#262261)", padding:"0 24px", borderBottom:"1px solid rgba(255,255,255,.08)" }}>
      <div style={{ maxWidth:1160, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, padding:"9px 0" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <div style={{ background:"rgba(242,134,39,.15)", border:"1px solid rgba(242,134,39,.3)", borderRadius:8, padding:"4px 10px", display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
            <span style={{ fontSize:11 }}>⏳</span>
            <span style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:13, color:"#fff" }}>{days} days</span>
          </div>
          <span className="urgency-text" style={{ fontSize:13, color:"rgba(255,255,255,.85)", fontWeight:500, fontFamily:"'Nunito Sans',sans-serif" }}>
            until Workforce Pell takes effect. <strong>Is Your Institution Ready?</strong>
          </span>
        </div>
        <button onClick={onDashboard} style={{ background:"rgba(242,134,39,.9)", border:"1px solid rgba(242,134,39,.5)", borderRadius:6, padding:"5px 14px", fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:12, cursor:"pointer", color:"#fff", whiteSpace:"nowrap", flexShrink:0 }}>Get Free Sandbox →</button>
      </div>
    </div>
  );
}

// ─── ECOSYSTEM TABS ───────────────────────────────────────────────────────────
function PhoneShell({ children, statusColor = "#22c55e", label = "GoSprout" }) {
  return (
    <div style={{ width:220, margin:"0 auto", position:"relative", flexShrink:0 }}>
      {/* Phone frame */}
      <div style={{ background:"#0f0d2a", border:"2px solid rgba(255,255,255,.12)", borderRadius:36, padding:"14px 10px", boxShadow:"0 32px 80px rgba(0,0,0,.7), inset 0 0 0 1px rgba(255,255,255,.04)" }}>
        {/* Notch */}
        <div style={{ display:"flex", justifyContent:"center", marginBottom:10 }}>
          <div style={{ width:60, height:6, background:"rgba(255,255,255,.08)", borderRadius:3 }} />
        </div>
        {/* Status bar */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"0 6px 8px", marginBottom:4 }}>
          <div style={{ fontSize:9, color:"rgba(255,255,255,.4)", fontFamily:"'Montserrat',sans-serif" }}>9:41</div>
          <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", fontWeight:700, letterSpacing:".04rem" }}>{label}</div>
          <div style={{ display:"flex", gap:3, alignItems:"center" }}>
            <div style={{ width:12, height:6, border:"1px solid rgba(255,255,255,.25)", borderRadius:2, position:"relative" }}>
              <div style={{ position:"absolute", inset:"1px 1px 1px 1px", width:"70%", background:statusColor, borderRadius:1 }} />
            </div>
          </div>
        </div>
        {/* Screen content */}
        <div style={{ background:"#16143a", borderRadius:20, overflow:"hidden", minHeight:380 }}>
          {children}
        </div>
        {/* Home bar */}
        <div style={{ display:"flex", justifyContent:"center", marginTop:12 }}>
          <div style={{ width:48, height:4, background:"rgba(255,255,255,.15)", borderRadius:2 }} />
        </div>
      </div>
    </div>
  );
}

function LearnerAppMock() {
  const [activeLog, setActiveLog] = useState(null);
  return (
    <PhoneShell label="GoSprout" statusColor="#F28627">
      {/* App header */}
      <div style={{ background:"linear-gradient(160deg,#262261,#1b1844)", padding:"16px 14px 12px" }}>
        <div style={{ fontSize:9, color:"rgba(255,255,255,.4)", fontFamily:"'Montserrat',sans-serif", marginBottom:2 }}>GOOD MORNING</div>
        <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:15, color:"#fff", marginBottom:10 }}>Marcus T. 👋</div>
        {/* Progress ring row */}
        <div style={{ background:"rgba(255,255,255,.06)", borderRadius:12, padding:"10px 12px", display:"flex", alignItems:"center", gap:10 }}>
          <svg width="44" height="44" viewBox="0 0 44 44">
            <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="4"/>
            <circle cx="22" cy="22" r="18" fill="none" stroke="#F28627" strokeWidth="4"
              strokeDasharray={`${2*Math.PI*18}`} strokeDashoffset={`${2*Math.PI*18*.32}`}
              strokeLinecap="round" transform="rotate(-90 22 22)" />
            <text x="22" y="26" textAnchor="middle" fill="#F28627" fontSize="10" fontFamily="'Nunito Sans',sans-serif" fontWeight="800">68%</text>
          </svg>
          <div>
            <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:12, color:"#fff" }}>Program Progress</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,.4)", marginTop:1 }}>847 / 1,240 OJT hours logged</div>
            <div style={{ fontSize:9, color:"#F28627", marginTop:3, fontFamily:"'Montserrat',sans-serif" }}>On track · Est. completion Jun 14</div>
          </div>
        </div>
      </div>

      {/* Quick log section */}
      <div style={{ padding:"12px 14px" }}>
        <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".05rem", marginBottom:8 }}>Log Today's Hours</div>
        {[
          { task:"Electrical Wiring — Panel Install", hours:"4h 30m", comp:"ATA 21" },
          { task:"Safety Inspection Protocol", hours:"1h 45m", comp:"ATA 09" },
        ].map((t,i) => (
          <div key={i} onClick={()=>setActiveLog(activeLog===i?null:i)} style={{ background: activeLog===i ? "rgba(242,134,39,.1)" : "rgba(255,255,255,.04)", border:`1px solid ${activeLog===i?"rgba(242,134,39,.3)":"rgba(255,255,255,.07)"}`, borderRadius:10, padding:"10px 12px", marginBottom:8, cursor:"pointer", transition:"all .2s" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div style={{ fontSize:11, color:"rgba(255,255,255,.75)", fontWeight:600, lineHeight:1.3, maxWidth:130 }}>{t.task}</div>
              <div style={{ fontSize:10, color:"#F28627", fontFamily:"'Montserrat',sans-serif", fontWeight:700, flexShrink:0 }}>{t.hours}</div>
            </div>
            <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", marginTop:4, fontFamily:"'Montserrat',sans-serif" }}>{t.comp}</div>
            {activeLog===i && (
              <div style={{ marginTop:8, paddingTop:8, borderTop:"1px solid rgba(255,255,255,.08)" }}>
                <div style={{ background:"#F28627", borderRadius:6, padding:"6px", textAlign:"center", fontSize:10, color:"#fff", fontWeight:700, fontFamily:"'Montserrat',sans-serif" }}>✓ Submit for Mentor Review</div>
              </div>
            )}
          </div>
        ))}

        {/* Competency badges */}
        <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".05rem", marginBottom:8, marginTop:12 }}>Recent Competencies Earned</div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {["Conduit Bending","Wire Gauge Selection","Panel Labeling"].map(b => (
            <div key={b} style={{ background:"rgba(242,134,39,.1)", border:"1px solid rgba(242,134,39,.2)", borderRadius:20, padding:"4px 8px", fontSize:9, color:"#F28627", fontWeight:700, fontFamily:"'Montserrat',sans-serif" }}>✓ {b}</div>
          ))}
        </div>
      </div>
    </PhoneShell>
  );
}

function MentorAppMock() {
  const [approved, setApproved] = useState([false, false, false]);
  return (
    <PhoneShell label="GoSprout Mentor" statusColor="#22c55e">
      {/* Header */}
      <div style={{ background:"linear-gradient(160deg,#1a3a2a,#1b2a1b)", padding:"16px 14px 12px" }}>
        <div style={{ fontSize:9, color:"rgba(255,255,255,.4)", fontFamily:"'Montserrat',sans-serif", marginBottom:2 }}>MENTOR PORTAL</div>
        <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:15, color:"#fff", marginBottom:4 }}>Diana R. — Site Supervisor</div>
        <div style={{ display:"flex", gap:6 }}>
          <div style={{ background:"rgba(34,197,94,.15)", border:"1px solid rgba(34,197,94,.25)", borderRadius:20, padding:"3px 8px", fontSize:9, color:"#22c55e", fontWeight:700, fontFamily:"'Montserrat',sans-serif" }}>3 Pending Reviews</div>
          <div style={{ background:"rgba(255,255,255,.06)", borderRadius:20, padding:"3px 8px", fontSize:9, color:"rgba(255,255,255,.4)", fontFamily:"'Montserrat',sans-serif" }}>5 Apprentices</div>
        </div>
      </div>

      <div style={{ padding:"12px 14px" }}>
        <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".05rem", marginBottom:8 }}>Pending Sign-Offs</div>
        {[
          { name:"Marcus T.", task:"Panel Install — 4.5 hrs", urgency:"today" },
          { name:"Aisha W.", task:"Safety Protocol — 1.75 hrs", urgency:"today" },
          { name:"Jordan K.", task:"Wire Gauge Selection — 3h", urgency:"yesterday" },
        ].map((r,i) => (
          <div key={i} style={{ background: approved[i] ? "rgba(34,197,94,.06)" : "rgba(255,255,255,.04)", border:`1px solid ${approved[i]?"rgba(34,197,94,.2)":"rgba(255,255,255,.07)"}`, borderRadius:10, padding:"10px 12px", marginBottom:7, transition:"all .3s" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,.8)", fontWeight:700 }}>{r.name}</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,.4)", marginTop:2 }}>{r.task}</div>
              </div>
              {approved[i] ? (
                <div style={{ fontSize:16 }}>✅</div>
              ) : (
                <button onClick={()=>setApproved(a=>{const n=[...a];n[i]=true;return n;})} style={{ background:"#22c55e", border:"none", color:"#fff", fontSize:9, fontFamily:"'Montserrat',sans-serif", fontWeight:700, padding:"5px 10px", borderRadius:6, cursor:"pointer" }}>APPROVE</button>
              )}
            </div>
            {!approved[i] && <div style={{ fontSize:9, color:"rgba(242,134,39,.6)", marginTop:4, fontFamily:"'Montserrat',sans-serif" }}>Due: {r.urgency}</div>}
          </div>
        ))}

        {/* Insight callout */}
        <div style={{ background:"rgba(242,134,39,.06)", border:"1px solid rgba(242,134,39,.15)", borderRadius:10, padding:"10px 12px", marginTop:10 }}>
          <div style={{ fontSize:10, color:"#F28627", fontWeight:700, marginBottom:3 }}>📊 Cohort Insight</div>
          <div style={{ fontSize:9, color:"rgba(255,255,255,.5)", lineHeight:1.5 }}>Marcus T. is 12% ahead of cohort average. Jordan K. needs 6 more hours this week to stay on track.</div>
        </div>
      </div>
    </PhoneShell>
  );
}

function EmployerPortalMock() {
  const [step, setStep] = useState(0);
  const steps = ["Company Info","Program Alignment","Team Access","Done ✓"];
  return (
    <div style={{ background:"#16143a", border:"1px solid rgba(255,255,255,.08)", borderRadius:18, overflow:"hidden", boxShadow:"0 32px 80px rgba(0,0,0,.6)" }}>
      {/* Browser chrome */}
      <div style={{ background:"#0f0d2a", padding:"10px 14px", display:"flex", alignItems:"center", gap:6, borderBottom:"1px solid rgba(255,255,255,.05)" }}>
        <div style={{ width:9, height:9, borderRadius:"50%", background:"#ef4444" }} />
        <div style={{ width:9, height:9, borderRadius:"50%", background:"#fbbf24" }} />
        <div style={{ width:9, height:9, borderRadius:"50%", background:"#22c55e" }} />
        <div style={{ flex:1, background:"rgba(255,255,255,.04)", borderRadius:5, padding:"4px 12px", marginLeft:8, fontSize:10, color:"rgba(255,255,255,.25)", fontFamily:"'Montserrat',sans-serif" }}>app.gosprout.io/employer/join/fcc-2026</div>
      </div>

      <div style={{ padding:"24px 28px" }}>
        {/* Header */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:10, color:"#F28627", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", marginBottom:6 }}>Employer Partner Onboarding</div>
          <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:18, color:"#fff", marginBottom:4 }}>Welcome to GoSprout</div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,.4)" }}>Frederick Community College · Workforce Pell Program · 3 steps to activate your employer portal</div>
        </div>

        {/* Progress steps */}
        <div style={{ display:"flex", alignItems:"center", marginBottom:24, gap:0 }}>
          {steps.map((s,i) => (
            <div key={s} style={{ display:"flex", alignItems:"center", flex: i < steps.length-1 ? 1 : "none" }}>
              <div onClick={()=>setStep(i)} style={{ width:26, height:26, borderRadius:"50%", background: i <= step ? "#F28627" : "rgba(255,255,255,.08)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color: i <= step ? "#fff" : "rgba(255,255,255,.3)", flexShrink:0, cursor:"pointer", transition:"all .2s", fontFamily:"'Montserrat',sans-serif" }}>
                {i < step ? "✓" : i+1}
              </div>
              {i < steps.length-1 && <div style={{ flex:1, height:1, background: i < step ? "#F28627" : "rgba(255,255,255,.08)", margin:"0 4px", transition:"background .3s" }} />}
            </div>
          ))}
        </div>

        {/* Step content */}
        {step === 0 && (
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,.7)", marginBottom:4 }}>Tell us about your company</div>
            {[["Company Name","Duncan Aviation"],["Industry","Aviation MRO"],["Location","Lincoln, NE"]].map(([l,p]) => (
              <div key={l}>
                <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".05rem", marginBottom:4 }}>{l}</div>
                <div style={{ background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.08)", borderRadius:6, padding:"8px 10px", fontSize:12, color:"rgba(255,255,255,.5)", fontFamily:"'Montserrat',sans-serif" }}>{p}</div>
              </div>
            ))}
          </div>
        )}
        {step === 1 && (
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,.7)", marginBottom:12 }}>Which programs are you hiring for?</div>
            {["Aviation Maintenance Tech","Aircraft Structural Repair","Avionics Systems"].map((prog,i) => (
              <div key={prog} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", background: i<2?"rgba(242,134,39,.06)":"rgba(255,255,255,.03)", border:`1px solid ${i<2?"rgba(242,134,39,.2)":"rgba(255,255,255,.07)"}`, borderRadius:8, marginBottom:7 }}>
                <div style={{ width:14, height:14, borderRadius:3, background:i<2?"#F28627":"rgba(255,255,255,.08)", border:`1px solid ${i<2?"#F28627":"rgba(255,255,255,.15)"}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {i<2 && <span style={{ color:"#fff", fontSize:9 }}>✓</span>}
                </div>
                <span style={{ fontSize:12, color: i<2?"rgba(255,255,255,.8)":"rgba(255,255,255,.4)" }}>{prog}</span>
                {i<2 && <span style={{ marginLeft:"auto", fontSize:9, color:"#F28627", fontFamily:"'Montserrat',sans-serif" }}>HIRING</span>}
              </div>
            ))}
          </div>
        )}
        {step === 2 && (
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,.7)", marginBottom:12 }}>Who on your team needs access?</div>
            {[["Diana R.","Site Supervisor","Mentor Access"],["Tom B.","HR Manager","Hiring Dashboard"],["Carol S.","Program Director","Full Access"]].map(([name,role,access],i) => (
              <div key={name} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:8, marginBottom:7 }}>
                <div style={{ width:28, height:28, borderRadius:"50%", background:"rgba(242,134,39,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:800, color:"#F28627", flexShrink:0, fontFamily:"'Nunito Sans',sans-serif" }}>{name.split(" ").map(n=>n[0]).join("")}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,.75)", fontWeight:600 }}>{name}</div>
                  <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif" }}>{role}</div>
                </div>
                <div style={{ fontSize:9, color:"#F28627", fontFamily:"'Montserrat',sans-serif", fontWeight:700 }}>{access}</div>
              </div>
            ))}
          </div>
        )}
        {step === 3 && (
          <div style={{ textAlign:"center", padding:"16px 0" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🎉</div>
            <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:18, color:"#fff", marginBottom:8 }}>Portal Active</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,.4)", lineHeight:1.65, marginBottom:16 }}>Duncan Aviation is now connected to Frederick CC's Workforce Pell programs. Your team has been notified.</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {["View Apprentices","OJT Sign-offs","Hiring Pipeline","Compliance Reports"].map(a => (
                <div key={a} style={{ background:"rgba(242,134,39,.06)", border:"1px solid rgba(242,134,39,.15)", borderRadius:8, padding:"8px", fontSize:10, color:"#F28627", fontWeight:700, fontFamily:"'Montserrat',sans-serif" }}>{a}</div>
              ))}
            </div>
          </div>
        )}

        <button onClick={()=>setStep(s=>Math.min(s+1,3))} style={{ marginTop:16, width:"100%", background: step < 3 ? "#F28627" : "rgba(255,255,255,.06)", border:"none", color: step < 3 ? "#fff" : "rgba(255,255,255,.3)", fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:12, padding:"11px", borderRadius:8, cursor: step < 3 ? "pointer" : "default", transition:"all .2s" }}>
          {step === 0 ? "Continue →" : step === 1 ? "Set Up Team Access →" : step === 2 ? "Activate Portal →" : "✓ Onboarding Complete"}
        </button>
      </div>
    </div>
  );
}

function AdminInsightMock() {
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [hoveredStudent, setHoveredStudent] = useState(null);
  return (
    <div style={{ background:"#16143a", border:"1px solid rgba(255,255,255,.08)", borderRadius:18, overflow:"hidden", boxShadow:"0 32px 80px rgba(0,0,0,.6)" }}>
      {/* Browser chrome */}
      <div style={{ background:"#0f0d2a", padding:"10px 14px", display:"flex", alignItems:"center", gap:6, borderBottom:"1px solid rgba(255,255,255,.05)" }}>
        <div style={{ width:9, height:9, borderRadius:"50%", background:"#ef4444" }} />
        <div style={{ width:9, height:9, borderRadius:"50%", background:"#fbbf24" }} />
        <div style={{ width:9, height:9, borderRadius:"50%", background:"#22c55e" }} />
        <div style={{ flex:1, background:"rgba(255,255,255,.04)", borderRadius:5, padding:"4px 12px", marginLeft:8, fontSize:10, color:"rgba(255,255,255,.25)", fontFamily:"'Montserrat',sans-serif" }}>app.gosprout.io/admin/retention-insights</div>
      </div>

      <div style={{ padding:"20px 24px" }}>
        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
          <div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".06rem", marginBottom:3 }}>RETENTION & PROACTIVE SUPPORT</div>
            <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:16, color:"#fff" }}>Frederick Community College</div>
          </div>
          <div style={{ background:"rgba(242,134,39,.1)", border:"1px solid rgba(242,134,39,.2)", borderRadius:8, padding:"5px 10px", fontSize:9, color:"#F28627", fontWeight:700, fontFamily:"'Montserrat',sans-serif" }}>Live · Updated 2m ago</div>
        </div>

        {/* Proactive alert — dismissible */}
        {!alertDismissed && (
          <div style={{ background:"rgba(251,168,74,.08)", border:"1px solid rgba(251,168,74,.25)", borderRadius:10, padding:"12px 14px", marginBottom:14, display:"flex", gap:10, alignItems:"flex-start" }}>
            <span style={{ fontSize:16, flexShrink:0 }}>⚠️</span>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,.85)", marginBottom:3 }}>Retention Alert — Action Recommended</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,.5)", lineHeight:1.5 }}>3 students in HVAC Fundamentals have logged fewer than 50% of required hours this week. Risk of SAP non-compliance in 12 days.</div>
              <button style={{ marginTop:8, background:"#fba84a", border:"none", color:"#1b1844", fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:9, padding:"5px 10px", borderRadius:5, cursor:"pointer" }}>Send Outreach → All 3</button>
            </div>
            <button onClick={()=>setAlertDismissed(true)} style={{ background:"none", border:"none", color:"rgba(255,255,255,.3)", cursor:"pointer", fontSize:12, flexShrink:0 }}>✕</button>
          </div>
        )}

        {/* At-risk students */}
        <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".05rem", marginBottom:8 }}>At-Risk Students — Early Warning</div>
        <div style={{ marginBottom:14 }}>
          {[
            { name:"Jordan K.", program:"HVAC Fundamentals", risk:"High", hours:"42%", gap:"Behind 18hrs", color:"#ef4444" },
            { name:"Priya M.", program:"CNA Certification", risk:"Med", hours:"61%", gap:"Behind 8hrs", color:"#fba84a" },
            { name:"Leon D.", program:"CDL Commercial", risk:"Low", hours:"74%", gap:"On track", color:"#22c55e" },
          ].map((s,i) => (
            <div key={s.name} onMouseEnter={()=>setHoveredStudent(i)} onMouseLeave={()=>setHoveredStudent(null)} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 10px", background: hoveredStudent===i ? "rgba(255,255,255,.05)" : "rgba(255,255,255,.02)", border:`1px solid ${hoveredStudent===i?"rgba(255,255,255,.1)":"rgba(255,255,255,.05)"}`, borderRadius:8, marginBottom:6, cursor:"pointer", transition:"all .15s" }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:s.color, flexShrink:0 }} />
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:"rgba(255,255,255,.75)", fontWeight:600 }}>{s.name}</div>
                <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif" }}>{s.program}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:11, color:s.color, fontWeight:700, fontFamily:"'Montserrat',sans-serif" }}>{s.hours} hrs</div>
                <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif" }}>{s.gap}</div>
              </div>
              {hoveredStudent===i && <div style={{ fontSize:9, color:"#F28627", fontFamily:"'Montserrat',sans-serif", fontWeight:700, flexShrink:0 }}>Contact →</div>}
            </div>
          ))}
        </div>

        {/* Insight metrics row */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
          {[
            { label:"Avg. Engagement", val:"83%", sub:"This week", color:"#F28627" },
            { label:"Interventions Sent", val:"12", sub:"This month", color:"#fba84a" },
            { label:"Retention Rate", val:"94%", sub:"vs 71% sector avg", color:"#22c55e" },
          ].map((m,i) => (
            <div key={m.label} style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.06)", borderRadius:8, padding:"10px 10px", textAlign:"center" }}>
              <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:18, color:m.color, lineHeight:1, marginBottom:3 }}>{m.val}</div>
              <div style={{ fontSize:9, color:"rgba(255,255,255,.5)", marginBottom:2 }}>{m.label}</div>
              <div style={{ fontSize:8, color:"rgba(255,255,255,.25)", fontFamily:"'Montserrat',sans-serif" }}>{m.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const ECOSYSTEM_TABS = [
  {
    id: "learner",
    icon: "📱",
    label: "Learner App",
    tag: "iOS & Android",
    headline: "Data from the Field, Not from Memory.",
    body: "Learners log OJT hours, competencies, and progress directly from the job site — in real time, on their phone. No paper timesheets. No end-of-week guessing. The data is accurate because it's captured the moment it happens.",
    bullets: [
      "Real-time OJT hour logging with task and competency tagging",
      "Progress tracking against program milestones and completion targets",
      "Competency badge earning — visible to students and coordinators",
      "Mentor review requests sent instantly from the field",
    ],
    cta: "See it in action",
  },
  {
    id: "mentor",
    icon: "🤝",
    label: "Mentor App",
    tag: "iOS & Android",
    headline: "Sign-Offs in Seconds, Not Spreadsheets.",
    body: "Mentors and site supervisors approve hours, sign off on competencies, and monitor apprentice progress from their phone — without logging into a portal or filling out forms. Friction-free for employers means compliance data that actually gets captured.",
    bullets: [
      "One-tap hour and competency approvals from anywhere",
      "Cohort-level insights showing which apprentices need attention",
      "Real-time alerts when apprentices fall behind pacing targets",
      "Direct line to coordinators without leaving the app",
    ],
    cta: "See it in action",
  },
  {
    id: "employer",
    icon: "🏭",
    label: "Employer Portal",
    tag: "Self-Serve Onboarding",
    headline: "Employers Activate in Minutes, Not Months.",
    body: "Most platforms treat employer onboarding as a coordinator task. GoSprout lets employer partners self-serve — they join via a unique link, align to programs, set up team access, and are connected to the institution's Workforce Pell programs without a single IT ticket or implementation call.",
    bullets: [
      "Self-serve onboarding via unique institution link — no coordinator needed",
      "Program alignment selection so employers opt into the right pathways",
      "Role-based team access: mentor, hiring manager, or program director",
      "Instant visibility into apprentice progress, OJT hours, and outcomes",
    ],
    cta: "See the flow",
  },
  {
    id: "admin",
    icon: "📊",
    label: "Admin Dashboard",
    tag: "Proactive Retention Intelligence",
    headline: "Stop Reacting. Start Predicting.",
    body: "GoSprout's admin dashboard doesn't just show you what happened — it tells you what's about to happen. Retention risk flags, engagement drop-off alerts, and SAP early-warning systems give coordinators time to intervene before a student falls out of compliance or drops the program.",
    bullets: [
      "At-risk student identification before SAP violations occur",
      "One-click outreach to students flagged for low engagement or hours",
      "Employer participation monitoring — know when mentors go silent",
      "Program-level retention benchmarks vs. sector averages",
    ],
    cta: "See the dashboard",
  },
];

function EcosystemTabs({ onDashboard }) {
  const [active, setActive] = useState(0);
  const tab = ECOSYSTEM_TABS[active];
  const [ref, inView] = useInView(0.1);

  return (
    <div ref={ref} style={{ opacity:inView?1:0, transform:inView?"translateY(0)":"translateY(28px)", transition:"opacity .8s, transform .8s" }}>
      {/* Tab row */}
      <div style={{ display:"flex", gap:8, marginBottom:36, flexWrap:"wrap" }}>
        {ECOSYSTEM_TABS.map((t,i) => (
          <button key={t.id} onClick={()=>setActive(i)} style={{ display:"flex", alignItems:"center", gap:8, background: active===i ? "rgba(242,134,39,.12)" : "rgba(255,255,255,.04)", border:`1px solid ${active===i?"rgba(242,134,39,.35)":"rgba(255,255,255,.08)"}`, borderRadius:10, padding:"10px 18px", cursor:"pointer", transition:"all .2s", flexShrink:0 }}>
            <span style={{ fontSize:16 }}>{t.icon}</span>
            <div style={{ textAlign:"left" }}>
              <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:13, color: active===i ? "#fff" : "rgba(255,255,255,.5)" }}>{t.label}</div>
              <div style={{ fontSize:9, color: active===i ? "#F28627" : "rgba(255,255,255,.25)", fontFamily:"'Montserrat',sans-serif", marginTop:1 }}>{t.tag}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Content area */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:48, alignItems:"center" }} className="two-col">
        {/* Left: copy */}
        <div>
          <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(242,134,39,.08)", border:"1px solid rgba(242,134,39,.15)", borderRadius:20, padding:"4px 12px", marginBottom:16 }}>
            <span style={{ fontSize:12 }}>{tab.icon}</span>
            <span style={{ fontSize:10, color:"#F28627", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", fontWeight:700 }}>{tab.tag}</span>
          </div>
          <h3 style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:"clamp(20px,2.5vw,32px)", lineHeight:1.15, color:"#fff", marginBottom:14 }}>{tab.headline}</h3>
          <p style={{ fontSize:15, color:"rgba(255,255,255,.5)", lineHeight:1.75, marginBottom:24 }}>{tab.body}</p>
          <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:28 }}>
            {tab.bullets.map((b,i) => (
              <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                <div style={{ width:18, height:18, background:"rgba(242,134,39,.12)", border:"1px solid rgba(242,134,39,.2)", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
                  <span style={{ fontSize:9, color:"#F28627" }}>✓</span>
                </div>
                <span style={{ fontSize:14, color:"rgba(255,255,255,.65)", lineHeight:1.55 }}>{b}</span>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <button className="btn-primary" style={{ fontSize:14, padding:"13px 24px" }} onClick={onDashboard}>🎯 Create My Free Sandbox</button>
            <button className="btn-ghost" style={{ fontSize:14, padding:"13px 24px" }} onClick={() => window.open(DEMO_URL,"_blank")}>See a Walkthrough</button>
          </div>
        </div>

        {/* Right: interactive mockup */}
        <div>
          {active === 0 && (
            <div style={{ display:"flex", gap:20, justifyContent:"center", alignItems:"flex-start" }}>
              <LearnerAppMock />
            </div>
          )}
          {active === 1 && (
            <div style={{ display:"flex", gap:20, justifyContent:"center", alignItems:"flex-start" }}>
              <MentorAppMock />
            </div>
          )}
          {active === 2 && <EmployerPortalMock />}
          {active === 3 && <AdminInsightMock />}
        </div>
      </div>

      {/* Bottom connector — data flow story */}
      <div style={{ marginTop:56, padding:"28px 32px", background:"rgba(255,255,255,.02)", border:"1px solid rgba(255,255,255,.06)", borderRadius:16 }}>
        <div style={{ fontSize:11, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", textAlign:"center", marginBottom:20 }}>How the Data Flows</div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:0, flexWrap:"wrap" }}>
          {[
            { icon:"📱", label:"Learner logs hours", sub:"in the field" },
            { arrow: true },
            { icon:"🤝", label:"Mentor approves", sub:"in one tap" },
            { arrow: true },
            { icon:"🏭", label:"Employer sees progress", sub:"in real time" },
            { arrow: true },
            { icon:"📊", label:"Admin gets insights", sub:"before problems arise" },
            { arrow: true },
            { icon:"🛡️", label:"Dashboard stays compliant", sub:"automatically" },
          ].map((node, i) => node.arrow ? (
            <div key={i} style={{ fontSize:16, color:"rgba(242,134,39,.4)", margin:"0 8px", flexShrink:0 }}>→</div>
          ) : (
            <div key={i} style={{ textAlign:"center", padding:"12px 14px", flexShrink:0 }}>
              <div style={{ fontSize:22, marginBottom:5 }}>{node.icon}</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,.65)", fontWeight:600, fontFamily:"'Nunito Sans',sans-serif" }}>{node.label}</div>
              <div style={{ fontSize:9, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", marginTop:2 }}>{node.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
export default function App() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => { document.body.style.overflow = showDashboard ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [showDashboard]);

  const scrollTo = (id) => { setMobileOpen(false); document.querySelector(id)?.scrollIntoView({ behavior:"smooth" }); };

  return (
    <div style={{ fontFamily:"'Nunito Sans',sans-serif", background:"#262261", color:"#fff", overflowX:"hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;600;700;800;900&family=Montserrat:wght@600;700;800&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        ::-webkit-scrollbar { width:6px; background:#1b1844; }
        ::-webkit-scrollbar-thumb { background:#F28627; border-radius:3px; }
        .orange { color:#F28627; }
        .white { color:#fff; }
        .mono { font-family:'Montserrat',sans-serif; }
        .eyebrow { font-family:'Montserrat',sans-serif; font-size:12px; font-weight:700; letter-spacing:.1rem; text-transform:uppercase; color:#F28627; }
        .btn-primary { background:#F28627; color:#fff; border:none; border-radius:8px; padding:14px 28px; font-family:'Montserrat',sans-serif; font-weight:700; font-size:14px; cursor:pointer; transition:background .2s, transform .15s; display:inline-block; }
        .btn-primary:hover { background:#bb612a; transform:translateY(-1px); }
        .btn-ghost { background:rgba(255,255,255,.08); color:#fff; border:1px solid rgba(255,255,255,.2); border-radius:8px; padding:14px 28px; font-family:'Montserrat',sans-serif; font-weight:700; font-size:14px; cursor:pointer; transition:all .2s; display:inline-block; }
        .btn-ghost:hover { background:rgba(255,255,255,.15); transform:translateY(-1px); }
        .section { padding:96px 24px; }
        .section-alt { background:#1b1844; }
        .container { max-width:1160px; margin:0 auto; }
        .divider { width:48px; height:3px; background:#F28627; border-radius:2px; margin:16px 0 32px; }
        .grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:24px; }
        .grid-3 { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; }
        .grid-4 { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
        .card { background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.08); border-radius:14px; transition:all .25s; }
        .card:hover { background:rgba(255,255,255,.1); border-color:rgba(242,134,39,.3); transform:translateY(-3px); }
        input[type=range] { -webkit-appearance:none; appearance:none; width:100%; height:6px; background:rgba(255,255,255,.15); border-radius:4px; outline:none; cursor:pointer; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance:none; width:20px; height:20px; border-radius:50%; background:#F28627; cursor:pointer; border:2px solid #fff; box-shadow:0 2px 6px rgba(0,0,0,.4); }
        input[type=range]::-moz-range-thumb { width:20px; height:20px; border-radius:50%; background:#F28627; cursor:pointer; border:2px solid #fff; }
        input[type=range]::-webkit-slider-runnable-track { height:6px; border-radius:4px; }
        .desktop-nav { display:flex; }
        @media(max-width:900px) {
          .grid-3,.grid-4{grid-template-columns:1fr 1fr;}
          .hero-cols,.two-col{grid-template-columns:1fr!important;}
          .grid-2{grid-template-columns:1fr;}
        }
        @media(max-width:640px) {
          .grid-3,.grid-4{grid-template-columns:1fr;}
          .grid-2{grid-template-columns:1fr;}
          .section{padding:56px 16px;}
          .desktop-nav{display:none!important;}
          .mobile-btn{display:block!important;}
          .btn-primary,.btn-ghost{width:100%;text-align:center;padding:14px 20px;font-size:14px;}
          .card{padding:20px 16px!important;}
        }
        @media(max-width:480px) {
          .section{padding:48px 14px;}
          .hero-cols{grid-template-columns:1fr!important;}
          .two-col{grid-template-columns:1fr!important;}
          .grid-3,.grid-4,.grid-2{grid-template-columns:1fr!important;}
        }
        .mobile-btn { display:none; }
        input::placeholder { color:rgba(255,255,255,.35); }
        select option { background:#1b1844; color:#fff; }

        /* Hero mobile fixes */
        @media(max-width:640px) {
          .hero-photo-col { display:none!important; }
          .hero-section { padding:48px 16px 56px!important; }
          .hero-badge { font-size:10px!important; padding:5px 12px!important; }
        }
        @media(max-width:560px) {
          .modal-inner { padding:18px 20px 28px!important; }
          .modal-grid { grid-template-columns:1fr!important; }
        }

        /* Ecosystem tabs mobile */
        @media(max-width:640px) {
          .eco-tab-row { flex-direction:column!important; }
          .eco-content { grid-template-columns:1fr!important; }
          .phone-shell { display:none!important; }
          .data-flow { flex-direction:column!important; align-items:flex-start!important; gap:4px!important; }
          .data-flow-arrow { transform:rotate(90deg); }
        }

        /* CTA strip mobile */
        @media(max-width:640px) {
          .cta-strip { grid-template-columns:1fr!important; text-align:center; margin:0 12px!important; padding:32px 24px!important; }
          .cta-strip button { width:100%; }
        }

        /* Dashboard table mobile */
        @media(max-width:640px) {
          .dash-table { display:none!important; }
          .dash-kpi { grid-template-columns:1fr 1fr!important; }
        }

        /* Compliance table mobile */
        @media(max-width:640px) {
          .compliance-table { grid-template-columns:1fr!important; }
          .compliance-header { display:none!important; }
        }

        /* Lifecycle grid mobile */
        @media(max-width:640px) {
          .lifecycle-grid { grid-template-columns:1fr 1fr!important; }
        }

        /* Consequence banner mobile */
        @media(max-width:640px) {
          .consequence-grid { grid-template-columns:1fr!important; }
        }

        /* Featured testimonial mobile */
        @media(max-width:640px) {
          .featured-testimonial { grid-template-columns:1fr!important; }
          .featured-testimonial-photo { display:none!important; }
        }

        /* Urgency bar mobile */
        @media(max-width:640px) {
          .urgency-text { display:none; }
        }
      `}</style>

      {showDashboard && <DashboardModal onClose={() => setShowDashboard(false)} />}

      {/* ── URGENCY BAR ── */}
      <UrgencyBar onDashboard={() => setShowDashboard(true)} />

      {/* ── NAV ── */}
      <nav style={{ position:"sticky", top:0, zIndex:100, padding:"0 24px", background:scrolled?"rgba(27,24,68,.97)":"transparent", backdropFilter:scrolled?"blur(20px)":"none", borderBottom:scrolled?"1px solid rgba(255,255,255,.08)":"none", transition:"all .3s" }}>
        <div style={{ maxWidth:1160, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", height:64 }}>
          <img src="/logo.png" alt="GoSprout" style={{ height:32, width:"auto", display:"block", mixBlendMode:"lighten" }} />
          <div className="desktop-nav" style={{ gap:2, alignItems:"center" }}>
            {NAV.map(n => (
              <button key={n.label} onClick={() => scrollTo(n.href)} style={{ background:"none", border:"none", color:"rgba(255,255,255,.55)", fontSize:13, fontFamily:"'Montserrat',sans-serif", fontWeight:600, cursor:"pointer", padding:"8px 13px", borderRadius:6, transition:"all .15s" }} onMouseEnter={e=>{e.target.style.color="#fff";e.target.style.background="rgba(255,255,255,.04)";}} onMouseLeave={e=>{e.target.style.color="rgba(255,255,255,.55)";e.target.style.background="none";}}>{n.label}</button>
            ))}
            <div style={{ width:1, height:18, background:"rgba(255,255,255,.08)", margin:"0 6px" }} />
            <button className="btn-ghost" style={{ padding:"9px 14px", fontSize:12 }} onClick={() => window.open(DEMO_URL,"_blank")}>See a Walkthrough</button>
            <button className="btn-primary" style={{ padding:"9px 18px", fontSize:13 }} onClick={() => setShowDashboard(true)}>Get Free Sandbox</button>
          </div>
          <button className="mobile-btn" onClick={() => setMobileOpen(!mobileOpen)} style={{ background:"none", border:"none", color:"#fff", fontSize:22, cursor:"pointer" }}>☰</button>
        </div>
        {mobileOpen && (
          <div style={{ background:"#1b1844", padding:"16px 24px 24px", borderTop:"1px solid rgba(255,255,255,.05)" }}>
            {NAV.map(n => <button key={n.label} onClick={() => scrollTo(n.href)} style={{ display:"block", background:"none", border:"none", color:"rgba(255,255,255,.8)", fontSize:15, fontFamily:"'Nunito Sans',sans-serif", fontWeight:600, cursor:"pointer", padding:"10px 0", width:"100%", textAlign:"left" }}>{n.label}</button>)}
            <button className="btn-primary" style={{ marginTop:12, width:"100%" }} onClick={() => { setShowDashboard(true); setMobileOpen(false); }}>🎯 Get Free Sandbox →</button>
            <button className="btn-ghost" style={{ marginTop:10, width:"100%" }} onClick={() => window.open(DEMO_URL,"_blank")}>See a Workforce Pell Walkthrough</button>
          </div>
        )}
      </nav>

      {/* ══════════════════════════════════════════════════════════════════════
          HERO — Opportunity-first, two-col with human photo
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="hero-section" style={{ padding:"96px 24px 88px", position:"relative", overflow:"hidden", background:"linear-gradient(160deg,#262261 0%,#1b1844 60%,#16143a 100%)" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:"linear-gradient(rgba(255,255,255,.018) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.018) 1px,transparent 1px)", backgroundSize:"64px 64px", pointerEvents:"none" }} />
        <div className="container" style={{ position:"relative" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 420px", gap:56, alignItems:"center" }} className="hero-cols">
            {/* Left: copy */}
            <FadeIn>
              <div className="hero-badge" style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(242,134,39,.09)", border:"1px solid rgba(242,134,39,.2)", borderRadius:20, padding:"6px 16px", marginBottom:24 }}>
                <span style={{ fontSize:13 }}>🏛️</span>
                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"#F28627", letterSpacing:".08rem", textTransform:"uppercase", fontWeight:500 }}>Federal Funding for Workforce Programs · July 1, 2026</span>
              </div>
              <h1 style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:"clamp(34px,5vw,64px)", lineHeight:1.06, letterSpacing:"-.025em", marginBottom:22 }}>
                Get Your Workforce Pell<br /><span className="orange">Program Sandbox.</span>
              </h1>
              <p style={{ fontSize:17, lineHeight:1.75, color:"rgba(255,255,255,.58)", maxWidth:520, marginBottom:16 }}>
                See exactly how your institution could manage Workforce Pell programs, employer partnerships, compliance requirements, and outcome reporting in under 5 minutes.
              </p>
              <p style={{ fontSize:15, lineHeight:1.7, color:"rgba(255,255,255,.36)", maxWidth:500, marginBottom:28 }}>
                No credit card. No implementation. No commitment. Instant access to your personalized dashboard.
              </p>
              <div style={{ background:"rgba(242,134,39,.06)", border:"1px solid rgba(242,134,39,.15)", borderRadius:14, padding:"18px 20px", marginBottom:28 }}>
                <div style={{ fontSize:11, color:"#F28627", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", marginBottom:12 }}>In Less Than 5 Minutes You'll Receive</div>
                {["✓ Workforce Pell Readiness Score (with gap analysis)","✓ Enrollment Opportunity Calculator","✓ Executive Workforce Pell Dashboard","✓ Compliance Readiness Assessment","✓ Workforce Pell Readiness Toolkit"].map(b => (
                  <div key={b} style={{ fontSize:13, color:"rgba(255,255,255,.7)", fontWeight:500, marginBottom:7, lineHeight:1.4 }}>{b}</div>
                ))}
              </div>
              <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:36 }}>
                <button className="btn-primary" style={{ fontSize:15, padding:"16px 30px" }} onClick={() => setShowDashboard(true)}>🎯 Create My Free Sandbox →</button>
                <button className="btn-ghost" style={{ fontSize:15, padding:"16px 30px" }} onClick={() => window.open(DEMO_URL,"_blank")}>See a Walkthrough</button>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                <span style={{ fontSize:11, color:"rgba(255,255,255,.25)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".06rem" }}>Trusted by</span>
                {["United Airlines","M-DCPS Technical Colleges","Frederick CC","Pearce Renewables","Miami EdTech","Duncan Aviation","Volvo Trucks","Amgen","Johnson & Johnson","& more"].map(n => (
                  <span key={n} style={{ fontSize:11, color:"rgba(255,255,255,.35)", background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.06)", borderRadius:20, padding:"3px 9px", fontWeight:500 }}>{n}</span>
                ))}
              </div>
            </FadeIn>

            {/* Right: photo + floating quote */}
            <FadeIn delay={0.12}>
              <div style={{ position:"relative" }} className="hero-photo-col">
                {/* PHOTO PLACEHOLDER — replace src with a real photo of a workforce coordinator or student */}
                <PhotoBlock
                  src="/hero-photo.png"
                  alt="Workforce program coordinator"
                  height={380}
                  label="Hero photo"
                />
                {/* Floating quote card */}
                <div style={{ position:"absolute", bottom:-20, left:-20, right:20, background:"rgba(7,9,23,.95)", border:"1px solid rgba(242,134,39,.2)", borderRadius:14, padding:"18px 20px", backdropFilter:"blur(12px)", boxShadow:"0 20px 60px rgba(0,0,0,.6)" }}>
                  <div style={{ fontSize:13, color:"rgba(255,255,255,.6)", lineHeight:1.6, fontStyle:"italic", marginBottom:14 }}>
                    "GoSprout has revolutionized how we manage our apprenticeship programs. The compliance workflows alone saved us hundreds of hours."
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    {/* PHOTO PLACEHOLDER — replace src with Carla Milan headshot */}
                    <Avatar src="/avatar-carla-milan.png" name="Carla Milan" initials="CM" color="#F28627" size={36} />
                    <div>
                      <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:13, color:"#fff" }}>Carla Milan</div>
                      <div style={{ fontSize:11, color:"#F28627" }}>Apprenticeship Coordinator · Frederick Community College</div>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 1 — THE OPPORTUNITY (Revenue before compliance)
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="opportunity" className="section section-alt">
        <div className="container">
          <FadeIn>
            <div className="eyebrow">The Opportunity</div>
            <div className="divider" />
            <h2 style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:"clamp(26px,4vw,44px)", lineHeight:1.12, maxWidth:680, marginBottom:14 }}>
              What Could Workforce Pell Mean <span className="orange">For Your Institution?</span>
            </h2>
            <p style={{ fontSize:16, color:"rgba(255,255,255,.45)", maxWidth:580, marginBottom:44, lineHeight:1.7 }}>
              Slide the eligibility estimate to see your institution's potential impact. These aren't projections. They're the revenue already sitting in your current short-term enrollment, newly fundable under Workforce Pell.
            </p>
          </FadeIn>
          <OpportunityCalc />
          {/* Macro stats */}
          <div className="grid-4" style={{ marginTop:40 }}>
            {[
              { num:"2.2M", label:"new students potentially eligible for Workforce Pell", src:"ACE Analysis" },
              { num:"$1.3B", label:"in additional Pell funding expected to flow to workforce programs", src:"CLASP Brief" },
              { num:"5,000+", label:"short-term programs newly Pell-eligible across institutions", src:"ED Estimate" },
              { num:"Jul 2026", label:"effective date: institutions must be operationally ready now", src:"GEN-24-01" },
            ].map((s,i) => (
              <FadeIn key={i} delay={i*.06}>
                <div className="card" style={{ padding:"22px 18px" }}>
                  <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:28, color:"#F28627", lineHeight:1, marginBottom:8 }}>{s.num}</div>
                  <div style={{ fontSize:13, color:"rgba(255,255,255,.5)", lineHeight:1.55, marginBottom:6 }}>{s.label}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,.2)", fontFamily:"'Montserrat',sans-serif" }}>Source: {s.src}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 2 — WORKFORCE PELL + WORK-BASED LEARNING (Differentiator)
          Moved here: GoSprout's unique advantage earns second position —
          right after the visitor cares about the opportunity.
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="section" style={{ background:"radial-gradient(ellipse 70% 40% at 50% 0%,rgba(242,134,39,.03) 0%,transparent 65%)" }}>
        <div className="container">
          <FadeIn>
            <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(242,134,39,.08)", border:"1px solid rgba(242,134,39,.2)", borderRadius:20, padding:"5px 14px", marginBottom:14 }}>
              <span style={{ fontSize:11, color:"#F28627", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", fontWeight:500 }}>GoSprout's Unique Advantage</span>
            </div>
            <div className="divider" style={{ background:"linear-gradient(90deg,#e07822,#F28627)" }} />
            <h2 style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:"clamp(26px,4vw,44px)", lineHeight:1.12, maxWidth:720, marginBottom:14 }}>
              Workforce Pell Rewards Programs That Lead to Jobs.<br /><span style={{ color:"#F28627" }}>GoSprout Connects the Dots.</span>
            </h2>
            <p style={{ fontSize:16, color:"rgba(255,255,255,.45)", maxWidth:620, marginBottom:48, lineHeight:1.7 }}>
              Most Workforce Pell conversations end at financial aid and compliance. GoSprout goes further — connecting work-based learning experiences directly to the placement and earnings outcomes that determine your program's long-term eligibility.
            </p>
          </FadeIn>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:32, alignItems:"center" }} className="two-col">
            <FadeIn>
              <div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,.35)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".06rem", marginBottom:20 }}>What Other Platforms Stop At</div>
                {["Financial Aid administration","Attendance tracking","Compliance checklists"].map((s,i) => (
                  <div key={i} style={{ display:"flex", gap:12, alignItems:"center", padding:"14px 0", borderBottom:"1px solid rgba(255,255,255,.04)" }}>
                    <span style={{ color:"rgba(239,68,68,.4)", fontSize:14 }}>✕</span>
                    <span style={{ fontSize:15, color:"rgba(255,255,255,.35)" }}>{s}</span>
                  </div>
                ))}
                <div style={{ height:16 }} />
                <div style={{ fontSize:13, color:"#F28627", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".06rem", marginBottom:20 }}>What GoSprout Adds</div>
                {[
                  ["🏭","Employer-connected OJT","Document on-the-job training hours, competencies, and supervisor approvals — the data GE metrics actually need"],
                  ["🤝","Apprenticeship & Internship Pathways","Embed work-based learning directly into short-term programs — improving placement rates and wage outcomes"],
                  ["📈","Placement & Wage Tracking","Track where graduates go and what they earn — per cohort, per employer, per program"],
                  ["🎯","Employer Alignment Verification","Prove to ED that every program maps to an in-demand occupation — with employer sign-offs, not just a spreadsheet"],
                ].map(([icon,title,desc],i) => (
                  <div key={i} style={{ display:"flex", gap:14, alignItems:"flex-start", padding:"16px 0", borderBottom:i<3?"1px solid rgba(255,255,255,.04)":"none" }}>
                    <div style={{ width:36, height:36, background:"rgba(242,134,39,.08)", border:"1px solid rgba(242,134,39,.15)", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, flexShrink:0 }}>{icon}</div>
                    <div>
                      <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:14, color:"rgba(255,255,255,.8)", marginBottom:4 }}>{title}</div>
                      <div style={{ fontSize:13, color:"rgba(255,255,255,.4)", lineHeight:1.55 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </FadeIn>
            <FadeIn delay={0.12}>
              <div style={{ background:"rgba(242,134,39,.04)", border:"1px solid rgba(242,134,39,.12)", borderRadius:18, padding:"30px 26px" }}>
                <div style={{ fontSize:12, color:"#F28627", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", marginBottom:18 }}>The Gainful Employment Logic</div>
                {[
                  { from:"Work-Based Learning", to:"Higher Completion Rates" },
                  { from:"Higher Completion", to:"Better Placement Rates" },
                  { from:"Better Placement", to:"Higher Wage Outcomes" },
                  { from:"Strong Wage Outcomes", to:"GE Compliance ✅" },
                  { from:"GE Compliance", to:"Sustained Pell Eligibility" },
                ].map((r,i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0", borderBottom:i<4?"1px solid rgba(242,134,39,.08)":"none" }}>
                    <div style={{ flex:1, fontSize:13, color:"rgba(255,255,255,.5)", fontWeight:500 }}>{r.from}</div>
                    <div style={{ fontSize:16, color:"#F28627", flexShrink:0 }}>→</div>
                    <div style={{ flex:1, fontSize:13, color:"rgba(255,255,255,.75)", fontWeight:600 }}>{r.to}</div>
                  </div>
                ))}
                <div style={{ marginTop:20, background:"rgba(242,134,39,.08)", border:"1px solid rgba(242,134,39,.15)", borderRadius:10, padding:"14px 16px", fontSize:13, color:"rgba(255,255,255,.55)", lineHeight:1.65 }}>
                  GoSprout is the only workforce operations platform that tracks this full chain — from work-based learning activity to gainful employment outcomes — in one system.
                </div>
                <button className="btn-primary" style={{ marginTop:20, width:"100%", textAlign:"center", fontSize:14, padding:"14px" }} onClick={() => window.open(DEMO_URL,"_blank")}>See a Workforce Pell Walkthrough →</button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 3 — APPRENTICESHIP + WORKFORCE PELL
          The most defensible path to GE compliance that exists.
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="apprenticeship" className="section section-alt">
        <div className="container">
          <FadeIn>
            <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(242,134,39,.08)", border:"1px solid rgba(242,134,39,.2)", borderRadius:20, padding:"5px 14px", marginBottom:14 }}>
              <span style={{ fontSize:11, color:"#fba84a", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", fontWeight:500 }}>Most Powerful Combination in Workforce Ed</span>
            </div>
            <div className="divider" style={{ background:"linear-gradient(90deg,#F28627,#fba84a)" }} />
            <h2 style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:"clamp(26px,4vw,44px)", lineHeight:1.12, maxWidth:780, marginBottom:14 }}>
              Apprenticeship Is the <span style={{ color:"#fba84a" }}>Best-Performing</span><br />Workforce Pell Program Type. Period.
            </h2>
            <p style={{ fontSize:16, color:"rgba(255,255,255,.45)", maxWidth:660, marginBottom:52, lineHeight:1.75 }}>
              Workforce Pell requires employer alignment, job placement, and wage attainment. Registered Apprenticeship is the only program model that structurally guarantees all three: the employer is already committed, the job already exists, and the wage is contractually set from day one. No other short-term program type comes close to this level of GE compliance insurance. GoSprout manages both on one platform.
            </p>
          </FadeIn>

          {/* Why apprenticeship wins at GE */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:32, marginBottom:48, alignItems:"start" }} className="two-col">
            <FadeIn>
              <div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".06rem", marginBottom:20 }}>Why Apprenticeship Dominates Gainful Employment Metrics</div>
                {[
                  { icon:"🤝", title:"Employer is already in the room", desc:"Sponsors commit to hiring before the program begins. Placement rates aren't tracked after the fact — they're built into the model. GE placement requirements are met by design, not by hope." },
                  { icon:"💵", title:"Wages are contractually set", desc:"Apprentices earn while they learn. Starting wages are defined in the apprenticeship agreement. ED's debt-to-earnings calculation is favorable almost automatically." },
                  { icon:"📋", title:"OJT is documented, not assumed", desc:"Every hour of on-the-job training is logged, supervisor-approved, and credential-mapped. This is exactly the documentation Workforce Pell auditors ask for — apprenticeship programs already have it." },
                  { icon:"🎓", title:"Credential attainment is structured", desc:"Apprenticeship programs lead to industry-recognized credentials, licenses, or journeyworker status. The Workforce Pell credential requirement is built into the model." },
                ].map((item, i) => (
                  <FadeIn key={i} delay={i * 0.06}>
                    <div style={{ display:"flex", gap:16, alignItems:"flex-start", padding:"20px 0", borderBottom:i < 3 ? "1px solid rgba(255,255,255,.05)" : "none" }}>
                      <div style={{ width:40, height:40, background:"rgba(242,134,39,.08)", border:"1px solid rgba(242,134,39,.15)", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{item.icon}</div>
                      <div>
                        <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:14, color:"rgba(255,255,255,.85)", marginBottom:6 }}>{item.title}</div>
                        <div style={{ fontSize:13, color:"rgba(255,255,255,.4)", lineHeight:1.65 }}>{item.desc}</div>
                      </div>
                    </div>
                  </FadeIn>
                ))}
              </div>
            </FadeIn>

            <FadeIn delay={0.1}>
              <div style={{ position:"sticky", top:88 }}>
                {/* Comparison card */}
                <div style={{ background:"rgba(242,134,39,.04)", border:"1px solid rgba(242,134,39,.15)", borderRadius:18, padding:"28px 26px", marginBottom:20 }}>
                  <div style={{ fontSize:12, color:"#fba84a", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", marginBottom:18 }}>GE Compliance Risk by Program Type</div>
                  {[
                    { type:"Registered Apprenticeship", risk:"Very Low", bar:95, color:"#F28627", note:"Employer-guaranteed placement + set wages" },
                    { type:"Industry Cert. Program", risk:"Low–Med", bar:65, color:"#F28627", note:"Depends on employer outreach after graduation" },
                    { type:"Short-Term Certificate", risk:"Medium", bar:45, color:"#FBBF24", note:"Placement tracking is manual and fragmented" },
                    { type:"Non-credit Workforce Trng.", risk:"High", bar:25, color:"#F87171", note:"Outcome data often missing or incomplete" },
                  ].map((r, i) => (
                    <div key={i} style={{ marginBottom:i < 3 ? 18 : 0 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                        <span style={{ fontSize:12, color:"rgba(255,255,255,.65)", fontWeight:500 }}>{r.type}</span>
                        <span style={{ fontSize:11, color:r.color, fontFamily:"'Montserrat',sans-serif", fontWeight:700 }}>{r.risk}</span>
                      </div>
                      <div style={{ height:6, background:"rgba(255,255,255,.06)", borderRadius:4, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${r.bar}%`, background:r.color, borderRadius:4, opacity:.8 }} />
                      </div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,.25)", marginTop:4, fontFamily:"'Montserrat',sans-serif" }}>{r.note}</div>
                    </div>
                  ))}
                </div>

                {/* Stat callout */}
                <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:14, padding:"22px 22px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                  {[
                    { val:"91%", label:"of apprentices still employed 9 months after completion", color:"#fba84a" },
                    { val:"$300K+", label:"average lifetime earnings advantage over non-apprentices", color:"#F28627" },
                  ].map((s, i) => (
                    <div key={i} style={{ textAlign:"center" }}>
                      <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:28, color:s.color, lineHeight:1, marginBottom:8 }}>{s.val}</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,.35)", lineHeight:1.5 }}>{s.label}</div>
                    </div>
                  ))}
                  <div style={{ gridColumn:"1 / -1", fontSize:10, color:"rgba(255,255,255,.2)", fontFamily:"'Montserrat',sans-serif", textAlign:"center", borderTop:"1px solid rgba(255,255,255,.05)", paddingTop:12 }}>
                    Source: U.S. Department of Labor, Apprenticeship USA
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* How GoSprout manages the full lifecycle */}
          <FadeIn delay={0.1}>
            <div style={{ background:"rgba(242,134,39,.03)", border:"1px solid rgba(242,134,39,.12)", borderRadius:20, padding:"36px 32px" }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:32, flexWrap:"wrap", gap:16 }}>
                <div>
                  <div style={{ fontSize:12, color:"#fba84a", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", marginBottom:10 }}>GoSprout Manages the Full Apprenticeship Lifecycle</div>
                  <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:18, color:"#fff", maxWidth:560, lineHeight:1.4 }}>From first day on the job to journeyworker status: every touchpoint tracked, every compliance requirement met, every Workforce Pell outcome documented.</div>
                </div>
                <button className="btn-primary" style={{ fontSize:13, padding:"12px 22px", whiteSpace:"nowrap", flexShrink:0 }} onClick={() => window.open(DEMO_URL,"_blank")}>See a Workforce Pell Walkthrough →</button>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:0, background:"rgba(255,255,255,.04)", borderRadius:12, overflow:"hidden" }} className="lifecycle-grid">
                {[
                  { phase:"Enrollment", icon:"📝", detail:"RAPIDS registration, Pell eligibility flagging, SAP baseline" },
                  { phase:"OJT Tracking", icon:"🔧", detail:"Clock-hour logging, supervisor sign-offs, ATA task mapping" },
                  { phase:"RTI Coordination", icon:"📚", detail:"Related Technical Instruction attendance + competency tracking" },
                  { phase:"Employer Portal", icon:"🏭", detail:"Employer dashboards, progress visibility, GE alignment docs" },
                  { phase:"SAP Monitoring", icon:"✅", detail:"Automated mid-program and end-program SAP compliance checks" },
                  { phase:"Outcomes & GE", icon:"📊", detail:"Placement, wage, and credential data — ED-ready at any time" },
                ].map((p, i) => (
                  <div key={i} style={{ padding:"20px 16px", borderRight:i < 5 ? "1px solid rgba(255,255,255,.06)" : "none", textAlign:"center" }}>
                    <div style={{ fontSize:22, marginBottom:10 }}>{p.icon}</div>
                    <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:12, color:"#fba84a", marginBottom:8, lineHeight:1.3 }}>{p.phase}</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,.35)", lineHeight:1.55 }}>{p.detail}</div>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Industries */}
          <FadeIn delay={0.15}>
            <div style={{ marginTop:32 }}>
              <div style={{ fontSize:12, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".06rem", marginBottom:16 }}>Industries Where Apprenticeship + Workforce Pell Is Already Working</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, alignItems:"start" }} className="two-col">
                <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                  {[
                    ["✈️","Aviation / MRO","United Airlines · Duncan Aviation"],
                    ["🏥","Healthcare","CNA · Medical Assistant · Phlebotomy"],
                    ["⚡","Clean Energy","Solar · Wind · HVAC · Electrician"],
                    ["🏗️","Construction","Plumbing · Welding · Ironworker"],
                    ["💻","Technology","Cybersecurity · AI · UX/UI · Data"],
                    ["🚛","Transportation","CDL · Logistics · Supply Chain"],
                  ].map(([icon, industry, examples], i) => (
                    <div key={i} style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:12, padding:"14px 18px", display:"flex", gap:12, alignItems:"flex-start", flex:"1 1 200px" }}>
                      <span style={{ fontSize:20, flexShrink:0 }}>{icon}</span>
                      <div>
                        <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:13, color:"rgba(255,255,255,.8)", marginBottom:3 }}>{industry}</div>
                        <div style={{ fontSize:11, color:"rgba(255,255,255,.3)", fontFamily:"'Montserrat',sans-serif" }}>{examples}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* PHOTO PLACEHOLDER — replace src with apprentice at work photo */}
                <div style={{ borderRadius:16, overflow:"hidden" }}>
                  <PhotoBlock
                    src="/apprentice-photo.png"
                    alt="Apprentice at work"
                    height={280}
                    label="Apprentice photo"
                  />
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 4 — WHY INSTITUTIONS FAIL (Urgency + fear)
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="challenge" className="section" style={{ background:"radial-gradient(ellipse 70% 40% at 50% 100%,rgba(239,68,68,.03) 0%,transparent 70%)" }}>
        <div className="container">
          <FadeIn>
            <div className="eyebrow">The Risk</div>
            <div className="divider" />
            <h2 style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:"clamp(26px,4vw,44px)", lineHeight:1.12, maxWidth:700, marginBottom:14 }}>
              Why Most Institutions Will Fail<br /><span className="orange">at Workforce Pell</span>
            </h2>
            <p style={{ fontSize:16, color:"rgba(255,255,255,.45)", maxWidth:600, marginBottom:48, lineHeight:1.7 }}>
              The biggest mistake colleges make is treating Workforce Pell as a funding program. It is actually a compliance program that happens to unlock funding. The distinction is everything.
            </p>
          </FadeIn>

          {/* Belief vs Reality cards */}
          <div className="grid-2" style={{ gap:20, marginBottom:48 }}>
            {FAILURE_MODES.map((f,i) => (
              <FadeIn key={i} delay={i*.07}>
                <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:16, padding:"26px 24px", position:"relative", overflow:"hidden" }}>
                  <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:"linear-gradient(90deg,rgba(239,68,68,.4),rgba(239,68,68,.1))" }} />
                  <div style={{ fontSize:22, marginBottom:14 }}>{f.icon}</div>
                  <div style={{ fontSize:11, fontFamily:"'Montserrat',sans-serif", color:"rgba(239,68,68,.6)", textTransform:"uppercase", letterSpacing:".06rem", marginBottom:8 }}>Common Belief</div>
                  <div style={{ fontSize:15, fontWeight:600, color:"rgba(255,255,255,.5)", marginBottom:16, fontStyle:"italic" }}>"{f.belief}"</div>
                  <div style={{ height:1, background:"rgba(255,255,255,.05)", marginBottom:16 }} />
                  <div style={{ fontSize:11, fontFamily:"'Montserrat',sans-serif", color:"#F28627", textTransform:"uppercase", letterSpacing:".06rem", marginBottom:8 }}>The Reality</div>
                  <div style={{ fontSize:14, color:"rgba(255,255,255,.7)", lineHeight:1.6 }}>{f.reality}</div>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Consequence banner */}
          <FadeIn delay={0.2}>
            <div style={{ background:"rgba(239,68,68,.05)", border:"1px solid rgba(239,68,68,.15)", borderRadius:16, padding:"28px 32px", display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:20 }} className="consequence-grid">
              <div style={{ fontSize:12, fontFamily:"'Montserrat',sans-serif", color:"rgba(239,68,68,.6)", textTransform:"uppercase", letterSpacing:".07rem", gridColumn:"1 / -1", marginBottom:4 }}>Consequences of Non-Compliance</div>
              {[
                { icon:"🚫", title:"Loss of Eligibility", desc:"Programs failing GE metrics 2 consecutive years are permanently removed from Workforce Pell." },
                { icon:"🔍", title:"Federal Audit Exposure", desc:"Without SAP automation and proper documentation, institutions face audit liability on every enrolled student." },
                { icon:"📉", title:"Enrollment Reversal", desc:"Students who enrolled expecting Pell aid and lose it mid-program are a retention, legal, and reputational risk." },
              ].map((c,i) => (
                <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                  <span style={{ fontSize:20, flexShrink:0 }}>{c.icon}</span>
                  <div>
                    <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:14, marginBottom:6, color:"rgba(255,255,255,.75)" }}>{c.title}</div>
                    <div style={{ fontSize:13, color:"rgba(255,255,255,.4)", lineHeight:1.55 }}>{c.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 3 — HOW GOSPROUT HELPS (Launch / Manage / Measure / Scale)
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="how" className="section section-alt">
        <div className="container">
          <FadeIn>
            <div className="eyebrow">Why GoSprout Is Different</div>
            <div className="divider" />
            <h2 style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:"clamp(26px,4vw,44px)", lineHeight:1.12, maxWidth:680, marginBottom:14 }}>
              Most Vendors Help You Track Learning.<br /><span className="orange">GoSprout Helps You Run Programs.</span>
            </h2>
            <p style={{ fontSize:16, color:"rgba(255,255,255,.45)", maxWidth:580, marginBottom:52, lineHeight:1.7 }}>
              Most LMSs and student information systems were built for credit-hour, semester-based education. Workforce Pell requires something different: a platform built around workforce outcomes, employer relationships, and real-time compliance.
            </p>
          </FadeIn>

          {/* 4-pillar grid */}
          <div className="grid-4" style={{ marginBottom:48 }}>
            {[
              { verb:"Launch", color:"#F28627", icon:"🗺️", what:"Build Workforce Pell pathways", how:"Configure short-term programs with clock-hour tracking, credential mapping, and employer alignment documentation — all required for eligibility." },
              { verb:"Manage", color:"#F28627", icon:"⚙️", what:"Coordinate every stakeholder", how:"GoSprout connects students, faculty, employers, and financial aid into one unified workflow — with automated SAP checkpoints and real-time alerts." },
              { verb:"Measure", color:"#F28627", icon:"📊", what:"Track outcomes Workforce Pell requires", how:"Gainful employment metrics, credential attainment, placement rates, and wage outcomes — tracked per cohort, per program, audit-ready at any time." },
              { verb:"Scale", color:"#e07822", icon:"🚀", what:"Grow without growing admin burden", how:"Manage any number of Workforce Pell programs from one dashboard. Every new program inherits your compliance frameworks automatically." },
            ].map((p,i) => (
              <FadeIn key={p.verb} delay={i*.07}>
                <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:16, padding:"28px 22px", height:"100%", position:"relative", overflow:"hidden", transition:"all .25s", cursor:"default" }} onMouseEnter={e=>{e.currentTarget.style.borderColor=`${p.color}40`;e.currentTarget.style.background=`${p.color}06`;}} onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,.07)";e.currentTarget.style.background="rgba(255,255,255,.03)";}}>
                  <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${p.color},${p.color}40)` }} />
                  <div style={{ fontSize:28, marginBottom:12 }}>{p.icon}</div>
                  <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:22, color:p.color, lineHeight:1, marginBottom:6 }}>{p.verb}</div>
                  <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:600, fontSize:14, color:"rgba(255,255,255,.7)", marginBottom:12 }}>{p.what}</div>
                  <div style={{ fontSize:13, color:"rgba(255,255,255,.4)", lineHeight:1.65 }}>{p.how}</div>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Compliance burden table */}
          <FadeIn delay={0.1}>
            <div style={{ marginBottom:12 }}>
              <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:18, marginBottom:6 }}>The Compliance Reality — and How GoSprout Handles It</div>
              <div style={{ fontSize:14, color:"rgba(255,255,255,.4)", lineHeight:1.6 }}>These are the actual operational requirements. GoSprout automates every one of them.</div>
            </div>
            <div style={{ border:"1px solid rgba(255,255,255,.07)", borderRadius:16, overflow:"hidden" }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", background:"rgba(255,255,255,.04)", padding:"12px 22px", borderBottom:"1px solid rgba(255,255,255,.07)" }} className="compliance-header">
                <div style={{ fontSize:10, fontFamily:"'Montserrat',sans-serif", color:"rgba(239,68,68,.5)", textTransform:"uppercase", letterSpacing:".07rem" }}>❌ Without GoSprout</div>
                <div style={{ fontSize:10, fontFamily:"'Montserrat',sans-serif", color:"#F28627", textTransform:"uppercase", letterSpacing:".07rem" }}>✅ With GoSprout</div>
              </div>
              {BURDENS.map((r,i) => (
                <FadeIn key={i} delay={i*.04}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", padding:"16px 22px", borderBottom:i<BURDENS.length-1?"1px solid rgba(255,255,255,.04)":"none", gap:24, background:i%2===1?"rgba(255,255,255,.015)":"transparent" }}>
                    <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                      <span style={{ color:"rgba(239,68,68,.5)", flexShrink:0, marginTop:2, fontSize:12 }}>✕</span>
                      <span style={{ fontSize:13, color:"rgba(255,255,255,.38)", lineHeight:1.55 }}>{r.problem}</span>
                    </div>
                    <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                      <span style={{ color:"#F28627", flexShrink:0, marginTop:2, fontSize:12 }}>→</span>
                      <span style={{ fontSize:13, color:"rgba(255,255,255,.72)", lineHeight:1.55, fontWeight:500 }}>{r.fix}</span>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 4 — OUTCOME DASHBOARD (The dream state)
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="dashboard" className="section" style={{ background:"radial-gradient(ellipse 80% 50% at 50% 50%,rgba(242,134,39,.04) 0%,transparent 65%)" }}>
        <div className="container">
          <FadeIn>
            <div className="eyebrow">The Future State</div>
            <div className="divider" />
            <h2 style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:"clamp(26px,4vw,44px)", lineHeight:1.12, maxWidth:680, marginBottom:14 }}>
              This Is What Your VP of Workforce<br /><span className="orange">Sees Every Morning.</span>
            </h2>
            <p style={{ fontSize:16, color:"rgba(255,255,255,.45)", maxWidth:580, marginBottom:48, lineHeight:1.7 }}>
              A single executive dashboard across every Workforce Pell program — enrollment, completion, placement, wage outcomes, employer participation, and compliance status. One view. No spreadsheets. No guessing.
            </p>
          </FadeIn>
          <DashboardMock />
          <FadeIn delay={0.2}>
            <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginTop:32, justifyContent:"center" }}>
              {["Real-time enrollment tracking","Automated SAP compliance","GE metric monitoring","Employer portal & OJT sign-offs","One-click ED export reports","Credential verification trail"].map(f => (
                <div key={f} style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, color:"rgba(255,255,255,.55)" }}>
                  <span style={{ color:"#F28627", fontSize:12 }}>✓</span>{f}
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION — THE ECOSYSTEM
          The dashboard is only as good as its data source.
          Mobile app → Employer portal → Admin dashboard
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="ecosystem" className="section section-alt">
        <div className="container">
          <FadeIn>
            <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(242,134,39,.08)", border:"1px solid rgba(242,134,39,.2)", borderRadius:20, padding:"5px 14px", marginBottom:14 }}>
              <span style={{ fontSize:11, color:"#F28627", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".07rem", fontWeight:500 }}>The GoSprout Ecosystem</span>
            </div>
            <div className="divider" />
            <h2 style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:"clamp(26px,4vw,48px)", lineHeight:1.1, maxWidth:780, marginBottom:14 }}>
              The Dashboard Is Only as Good as<br /><span className="orange">the Data Feeding It.</span>
            </h2>
            <p style={{ fontSize:16, color:"rgba(255,255,255,.45)", maxWidth:640, marginBottom:56, lineHeight:1.75 }}>
              Most platforms ask coordinators to manually enter data after the fact. GoSprout captures it at the source, from learners in the field, mentors on the job site, and employer partners onboarding themselves, and surfaces it automatically in the admin dashboard before problems become crises.
            </p>
          </FadeIn>

          {/* ── TAB SWITCHER ── */}
          <EcosystemTabs onDashboard={() => setShowDashboard(true)} />
        </div>
      </section>


      <section className="section">
        <div className="container">
          <FadeIn>
            <div className="eyebrow">Outcomes</div>
            <div className="divider" />
            <h2 style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:"clamp(24px,3.5vw,40px)", lineHeight:1.15, maxWidth:620, marginBottom:48 }}>
              The Institutions Building the Future of Workforce Ed <span className="orange">Already Use GoSprout</span>
            </h2>
          </FadeIn>

          {/* Stats row */}
          <div className="grid-3" style={{ marginBottom:52 }}>
            {[
              { num:82, suf:"%", label:"Average program completion rate", sub:"vs. ~55% industry average for short-term programs" },
              { num:50, suf:"%", label:"Reduction in compliance admin time", sub:"Reported by coordinators within the first 90 days" },
              { num:98, suf:"%", label:"Stakeholder satisfaction score", sub:"Across employers, students, and program administrators" },
            ].map((s,i) => (
              <FadeIn key={i} delay={i*.08}>
                <div className="card" style={{ padding:"32px 26px", textAlign:"center" }}>
                  <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:52, color:"#F28627", lineHeight:1, marginBottom:10 }}>
                    <AnimatedNumber target={s.num} suffix={s.suf} />
                  </div>
                  <div style={{ fontSize:14, color:"rgba(255,255,255,.6)", lineHeight:1.55, marginBottom:8 }}>{s.label}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,.28)", fontFamily:"'Montserrat',sans-serif" }}>{s.sub}</div>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Featured testimonial — large format with photo */}
          <FadeIn delay={0.05}>
            <div style={{ background:"rgba(242,134,39,.04)", border:"1px solid rgba(242,134,39,.2)", borderRadius:20, overflow:"hidden", display:"grid", gridTemplateColumns:"1fr 380px", marginBottom:24 }} className="two-col featured-testimonial">
              <div style={{ padding:"40px 40px" }}>
                <div style={{ fontSize:11, color:"#F28627", fontFamily:"'Montserrat',sans-serif", textTransform:"uppercase", letterSpacing:".08rem", marginBottom:18 }}>Featured — Technical College System</div>
                <div style={{ fontSize:20, lineHeight:1.7, color:"rgba(255,255,255,.8)", fontStyle:"italic", marginBottom:28, fontFamily:"'Nunito Sans',sans-serif", fontWeight:600 }}>
                  "GoSprout gave us the infrastructure to scale workforce programs we couldn't have managed manually. Our coordinators spend their time on students now, not spreadsheets."
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  {/* PHOTO PLACEHOLDER — replace src with Rene Mantilla headshot */}
                  <Avatar src="/avatar-rene-mantilla.png" name="Rene Mantilla" initials="RM" color="#F28627" size={52} />
                  <div>
                    <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:16, color:"#fff" }}>Rene Mantilla</div>
                    <div style={{ fontSize:13, color:"#fba84a", marginTop:3 }}>M-DCPS Technical Colleges</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,.3)", marginTop:2, fontFamily:"'Montserrat',sans-serif" }}>Miami-Dade County Public Schools · Florida</div>
                  </div>
                </div>
              </div>
              <div className="featured-testimonial-photo" style={{ position:"relative", minHeight:280 }}>
                {/* PHOTO PLACEHOLDER — replace src with a real photo of M-DCPS campus or program */}
                <PhotoBlock
                  src="/mdcps-program-photo.png"
                  alt="M-DCPS Technical College program"
                  height="100%"
                  label="M-DCPS program photo"
                />
              </div>
            </div>
          </FadeIn>

          {/* Three-up testimonial grid */}
          <div className="grid-3">
            {[
              {
                q:"GoSprout has revolutionized how we manage our apprenticeship programs. The compliance workflows alone saved us hundreds of hours — and we can finally prove outcomes to our accreditor.",
                name:"Carla Milan",
                title:"Apprenticeship Coordinator",
                org:"Frederick Community College",
                initials:"CM",
                color:"#F28627",
                // PHOTO PLACEHOLDER: replace src with Carla Milan headshot
                src: "/avatar-carla-milan.png",
              },
              {
                q:"GoSprout helped us build the infrastructure we need to scale high-quality apprenticeships with real accountability. It gave our teams the tools to track progress and retain talent.",
                name:"Scott Ferris",
                title:"Apprenticeship Director",
                org:"United Airlines",
                initials:"SF",
                color:"#1a4fa8",
                // PHOTO PLACEHOLDER: replace src with Scott Ferris headshot
                src: "/avatar-scott-ferris.png",
              },
              {
                q:"The visibility GoSprout gives us across programs is something we've never had before. We can see exactly where every student stands — and so can our employer partners.",
                name:"Jenae Harris",
                title:"Workforce Program Director",
                org:"Georgia State University",
                initials:"JH",
                color:"#003057",
                // PHOTO PLACEHOLDER: replace src with Jenae Harris headshot
                src: "/avatar-jenae-harris.png",
              },
            ].map((t,i) => (
              <FadeIn key={i} delay={i*.07}>
                <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:16, padding:"28px 24px", height:"100%", display:"flex", flexDirection:"column" }}>
                  <div style={{ fontSize:26, color:"rgba(242,134,39,.2)", fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, lineHeight:1, marginBottom:14 }}>"</div>
                  <div style={{ fontSize:14, color:"rgba(255,255,255,.58)", lineHeight:1.75, marginBottom:"auto", fontStyle:"italic", paddingBottom:20 }}>{t.q}</div>
                  <div style={{ borderTop:"1px solid rgba(255,255,255,.06)", paddingTop:16, display:"flex", alignItems:"center", gap:12 }}>
                    {/* Avatar — swap src when headshot available */}
                    <Avatar src={t.src} name={t.name} initials={t.initials} color={t.color} size={42} />
                    <div>
                      <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:13, color:"#fff" }}>{t.name}</div>
                      <div style={{ fontSize:12, color:"#F28627", marginTop:2 }}>{t.title}</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,.3)", marginTop:1, fontFamily:"'Montserrat',sans-serif" }}>{t.org}</div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          TOOLKIT CTA STRIP
      ══════════════════════════════════════════════════════════════════════ */}
      <div style={{ background:"linear-gradient(135deg,rgba(242,134,39,.1),rgba(242,134,39,.04))", border:"1px solid rgba(242,134,39,.18)", margin:"0 24px", borderRadius:20, padding:"44px 48px" }} className="cta-strip-wrap">
        <FadeIn>
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:40, alignItems:"center", maxWidth:1160, margin:"0 auto" }} className="two-col cta-strip">
            <div>
              <div style={{ fontSize:11, fontFamily:"'Montserrat',sans-serif", color:"#F28627", textTransform:"uppercase", letterSpacing:".08rem", marginBottom:10 }}>🎯 Free · Instant · No Credit Card</div>
              <h3 style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:"clamp(20px,3vw,32px)", lineHeight:1.2, marginBottom:10 }}>Get Your Workforce Pell Program Sandbox</h3>
              <p style={{ fontSize:14, color:"rgba(255,255,255,.45)", lineHeight:1.65, maxWidth:520, marginBottom:12 }}>See what Workforce Pell management could look like at your institution. In under 5 minutes you'll have a Readiness Score, Opportunity Calculator, Executive Dashboard, and Compliance Checklist.</p>
              <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
                {["No implementation","No commitment","Includes Readiness Toolkit"].map(t => (
                  <div key={t} style={{ fontSize:12, color:"rgba(255,255,255,.4)", display:"flex", alignItems:"center", gap:5 }}>
                    <span style={{ color:"#F28627" }}>✓</span>{t}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ flexShrink:0, textAlign:"center" }}>
              <button className="btn-primary" style={{ fontSize:15, padding:"16px 28px", whiteSpace:"nowrap" }} onClick={() => setShowDashboard(true)}>Create My Free Sandbox →</button>
              <div style={{ marginTop:9, fontSize:11, color:"rgba(255,255,255,.2)", fontFamily:"'Montserrat',sans-serif" }}>Also includes the Readiness Toolkit</div>
            </div>
          </div>
        </FadeIn>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          FINAL CTA
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="cta" className="section" style={{ textAlign:"center", background:"#16143a", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse 60% 55% at 50% 50%,rgba(242,134,39,.06) 0%,transparent 65%)", pointerEvents:"none" }} />
        <div className="container" style={{ position:"relative" }}>
          <FadeIn>
            <div style={{ display:"inline-block", marginBottom:24 }}><img src="/logo.png" alt="GoSprout" style={{ height:36, width:"auto", display:"block", mixBlendMode:"lighten" }} /></div>
            <h2 style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:800, fontSize:"clamp(28px,5vw,56px)", lineHeight:1.08, letterSpacing:"-.02em", marginBottom:18 }}>
              Don't Download Another PDF<br /><span className="orange">See Your Workforce Pell Operation in Action.</span>
            </h2>
            <p style={{ fontSize:17, color:"rgba(255,255,255,.45)", maxWidth:560, margin:"0 auto 20px", lineHeight:1.72 }}>
              Workforce Pell creates new expectations around employer engagement, learner progress, outcomes, and compliance. Explore a live GoSprout environment and see how institutions can launch, manage, measure, and scale Workforce Pell programs from day one.
            </p>
            <p style={{ fontSize:14, color:"rgba(255,255,255,.3)", maxWidth:440, margin:"0 auto 42px", lineHeight:1.65 }}>
              Or book a 30-minute walkthrough and we'll show you exactly how GoSprout operationalizes Workforce Pell compliance for your specific programs.
            </p>
            <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap", marginBottom:56 }}>
              <button className="btn-primary" style={{ fontSize:16, padding:"18px 38px" }} onClick={() => setShowDashboard(true)}>🎯 Create My Free Sandbox →</button>
              <button className="btn-ghost" style={{ fontSize:16, padding:"18px 38px" }} onClick={() => window.open(DEMO_URL,"_blank")}>See a Workforce Pell Walkthrough</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:1, maxWidth:740, margin:"0 auto", background:"rgba(255,255,255,.05)", borderRadius:16, overflow:"hidden", border:"1px solid rgba(255,255,255,.06)" }}>
              {[
                ["🗺️","Launch","Program setup, clock-hour config, credential mapping"],
                ["⚙️","Manage","SAP automation, employer portal, student tracking"],
                ["📊","Measure","GE metrics, completion, placement, wage outcomes"],
                ["📤","Report","One-click ED export, audit-ready documentation"],
                ["🤝","Coordinate","Faculty, employers, financial aid — unified"],
                ["🚀","Scale","Any number of programs, zero added admin burden"],
              ].map(([icon,title,desc],i) => (
                <div key={i} style={{ padding:"22px 18px", background:"#1b1844", borderRight:i%3<2?"1px solid rgba(255,255,255,.04)":"none", borderBottom:i<3?"1px solid rgba(255,255,255,.04)":"none" }}>
                  <div style={{ fontSize:20, marginBottom:8 }}>{icon}</div>
                  <div style={{ fontFamily:"'Nunito Sans',sans-serif", fontWeight:700, fontSize:13, color:"#F28627", marginBottom:6 }}>{title}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,.3)", lineHeight:1.55 }}>{desc}</div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background:"#0f0d2a", borderTop:"1px solid rgba(255,255,255,.06)", padding:"26px 24px" }}>
        <div className="container" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:14 }}>
          <img src="/logo.png" alt="GoSprout" style={{ height:24, width:"auto", display:"block", mixBlendMode:"lighten" }} />
          <div style={{ fontSize:11, color:"rgba(255,255,255,.18)", fontFamily:"'Montserrat',sans-serif" }}>© 2026 GoSprout · Workforce Pell Infrastructure · gosprout.app</div>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
            {[["StudentAid.gov","https://studentaid.gov/sa/about/announcements/blog/workforce-pell-grants"],["ED GEN-24-01","https://fsapartners.ed.gov/knowledge-center/library/dear-colleague-letters/2024-01-19/workforce-pell-grants-short-term-programs"],["ACE Fact Sheet","https://www.acenet.edu/Documents/ACE-Workforce-Pell-Fact-Sheet.pdf"],["CLASP Brief","https://www.clasp.org/publications/report/brief/workforce-pell-grants-brief/"]].map(([l,h]) => (
              <a key={l} href={h} target="_blank" rel="noopener noreferrer" style={{ fontSize:11, color:"rgba(255,255,255,.2)", textDecoration:"none", fontFamily:"'Montserrat',sans-serif", transition:"color .15s" }} onMouseEnter={e=>e.target.style.color="#F28627"} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,.2)"}>{l}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
